import React, { useRef, useEffect, useCallback, useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { EQ_IMAGES } from '../data/images';
import { snap, toShop, deg2rad } from '../utils/cadUtils';
import { pointInPolygon, getDoorEndpoints, getWallEndpoints, projectPointOntoWall, polygonCentroid, computeBbox, isSelfIntersecting } from '../utils/shopGeometry';
import { SIMULATABLE_LIFT_IDS } from '../utils/vehiclePath';
import VehiclePathOverlay from './VehiclePathOverlay';
import { findOverlaps } from '../utils/collision';

const C = {
  bg: '#03080f', grid: '#0d2a40', gridMaj: '#1a4060',
  wall: '#00ccff', wallGlow: '#00ccff55',
  dim: '#ff9900', dimTxt: '#ffaa22',
  overhead: '#ffcc00', door: '#00cc88',
  accent: '#3399ff', cross: 'rgba(0,180,255,0.1)',
  drain: '#0088aa', drainFill: 'rgba(0,150,200,0.12)',
  compassN: '#ff4444',
  measure: '#00ff88',
};

const PAD_L = 50;
const PAD_R = 120;
const PAD_T = 80;
const PAD_B = 100;

const ns = 'http://www.w3.org/2000/svg';
const mk = (tag, attrs = {}) => {
  const e = document.createElementNS(ns, tag);
  Object.entries(attrs).forEach(([k, v]) => e.setAttribute(k, v));
  return e;
};
const mkTxt = (x, y, txt, fill, fs = 9, anchor = 'middle') => {
  const t = mk('text', { x, y, fill, 'font-size': fs, 'font-family': 'monospace', 'text-anchor': anchor });
  t.textContent = txt; return t;
};

// Draws a small padlock icon at (x, y) inside the parent group.
// Used as a visual indicator that an item is locked (non-draggable).
// Counter-rotates against the item's rotation so the icon stays upright.
const drawLockBadge = (parent, x, y, parentRot = 0) => {
  const g = mk('g');
  g.setAttribute('transform', `translate(${x},${y}) rotate(${-parentRot})`);
  g.setAttribute('pointer-events', 'none');
  // Background circle so the icon stays readable on any color
  g.appendChild(mk('circle', { cx: 0, cy: 0, r: 7, fill: '#03080f', stroke: '#ffaa22', 'stroke-width': 1, opacity: 0.95 }));
  // Padlock body (small rounded rect)
  g.appendChild(mk('rect', { x: -3, y: -1, width: 6, height: 5, rx: 1, fill: '#ffaa22', opacity: 0.9 }));
  // Padlock shackle (arc)
  g.appendChild(mk('path', { d: 'M -2 -1 A 2 2 0 0 1 2 -1', fill: 'none', stroke: '#ffaa22', 'stroke-width': 1.2 }));
  parent.appendChild(g);
};

const SNAP_OPTIONS = [
  { label: 'None', value: 0 },
  { label: '3"',   value: 0.25 },
  { label: '6"',   value: 0.5 },
  { label: '1ft',  value: 1 },
  { label: '2ft',  value: 2 },
];

// Wrapped in forwardRef so the parent (App.js) can call resetZoom() from
// outside — used right before export so the exported view always shows the
// whole shop fitted/centered, regardless of whatever zoom/pan the user left
// the editor at. See resetZoom() below + useImperativeHandle at the bottom
// of the component body.
const CadCanvas = forwardRef(function CadCanvas({
  shop, setShop, doors, setDoors,
  placed, setPlaced, drains, setDrains,
  dimensions, setDimensions, selectedDim, setSelectedDim,
  selected, setSelected, selectedDrain, setSelectedDrain,
  simVehicleKey, setSimVehicleKey,
  simDoorId, setSimDoorId,
  simPathMode, setSimPathMode,
  simManualWaypoints, setSimManualWaypoints,
  simManualGoalGear, setSimManualGoalGear,
  simManualGoalFlip, setSimManualGoalFlip,
  simSpeed, setSimSpeed,
  onPlacementError, showToast, svgRef,
  // Undo/redo glue from App via useUndoableState
  commitHistory, undo, redo, canUndo, canRedo,
  // When true, suppress editing-only visuals (lock badges, mouse coord readout,
  // crosshairs). Used for export rendering so the saved PNG/PDF is clean.
  hideChrome = false,
  // Free-form room drawing mode — when true, clicks place polygon vertices
  // instead of selecting items. Toggled by the Sidebar "Add Free-form Room" button.
  roomDrawMode = false,
  setRoomDrawMode,
}, ref) {
  const areaRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [mouse, setMouse] = useState({ x: 0, y: 0, alt: false });
  const [snapSize, setSnapSize] = useState(1);
  const interactRef = useRef(null);
  const doorDragRef = useRef(null);
  const drainDragRef = useRef(null);
  const snapRef = useRef(1);
  const clipboardRef = useRef(null);
  const didDragRef = useRef(false);
  const [dragBox, setDragBox] = useState(null);
  const dragBoxRef = useRef(null);
  // Vehicle approach simulation panel — open/closed is local to this view
  // (the 3D view has its own independent toggle), but WHICH vehicle is
  // selected (simVehicleKey) comes from App so both views stay in sync.
  const [simActive, setSimActive] = useState(false);

  const [dimMode, setDimMode] = useState(false);
  const [dimStart, setDimStart] = useState(null);
  const dimModeRef = useRef(false);
  useEffect(() => { dimModeRef.current = dimMode; }, [dimMode]);

  // ─── Free-form room drawing state ─────────────────────────────────
  // Vertices collected as the user clicks corners. Each is in shop coords (feet).
  // Mirror ref so synchronous click handlers can read the latest values without
  // relying on stale React closure state (same pattern as dimMode/dimModeRef).
  const [roomDrawVerts, setRoomDrawVerts] = useState([]);
  const roomDrawVertsRef = useRef([]);
  const roomDrawModeRef = useRef(false);
  useEffect(() => { roomDrawVertsRef.current = roomDrawVerts; }, [roomDrawVerts]);
  useEffect(() => { roomDrawModeRef.current = roomDrawMode; }, [roomDrawMode]);
  // Clear in-progress vertices whenever drawing mode is exited (cancel, ESC, finish)
  useEffect(() => {
    if (!roomDrawMode) {
      setRoomDrawVerts([]);
      roomDrawVertsRef.current = [];
    }
  }, [roomDrawMode]);

  const [showGrid, setShowGrid] = useState(true);
  // Global toggle for bay clearance zones around lifts (toggled by B key / toolbar button).
  // Per-lift override: item.showBayZone can be true/false to override, undefined = follow global.
  const [showBayZones, setShowBayZones] = useState(false);

  // Bottom-bar dropdown open/close state. Only one ever needs to be open at a time
  // but keeping them as separate flags is simpler than a single 'openMenu' enum.
  const [showSnapMenu, setShowSnapMenu] = useState(false);
  const [showLabelsMenu, setShowLabelsMenu] = useState(false);
  const [showShortcutsMenu, setShowShortcutsMenu] = useState(false);

  useEffect(() => { snapRef.current = snapSize; }, [snapSize]);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef(null);
  const zoomRef = useRef(1);
  const panStateRef = useRef({ x: 0, y: 0 });
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { panStateRef.current = pan; }, [pan]);

  const availW = size.w - PAD_L - PAD_R;
  const availH = size.h - PAD_T - PAD_B;
  // Rectangle shops are always constructed with their origin at (0,0) — top
  // left of the bbox — so "fit shop.w x shop.h into the canvas" and "fit the
  // shop's actual bounding box" are the same thing for them. Free-form shops
  // are NOT guaranteed that: they're drawn in the setup page's own
  // independently pannable canvas, so their vertices can legitimately sit
  // anywhere (e.g. {x: -160, y: 165}) depending on where the user had
  // panned to while drawing — nothing renormalizes them back to (0,0)
  // afterward. Blindly assuming origin-at-top-left here (the old code) put
  // the shape's actual screen position wherever THAT offset happened to
  // fall relative to the assumed (0,0)-to-(w,h) frame — often far outside
  // the visible canvas. Computing the real bbox and centering on THAT
  // fixes it regardless of where the vertices happen to sit, and costs
  // nothing for rect shops since their bbox already starts at (0,0).
  const shopBbox = (shop.shapeType === 'free' && shop.vertices && shop.vertices.length >= 3)
    ? computeBbox(shop.vertices)
    : { minX: 0, minY: 0, w: shop.w, h: shop.h };
  const baseScale = Math.min(availW / shopBbox.w, availH / shopBbox.h, 20);
  const baseOx = PAD_L + (availW - shopBbox.w * baseScale) / 2 - shopBbox.minX * baseScale;
  const baseOy = PAD_T + (availH - shopBbox.h * baseScale) / 2 - shopBbox.minY * baseScale;
  const scale = baseScale * zoom;
  const ox = baseOx + pan.x + (shopBbox.w * baseScale / 2) * (1 - zoom);
  const oy = baseOy + pan.y + (shopBbox.h * baseScale / 2) * (1 - zoom);

  // Close the vehicle-approach panel if the selection moves off the lift
  // it was showing (or gets cleared) — avoids leaving it displaying a
  // stale simulation for whatever's newly selected.
  useEffect(() => {
    if (selected.length !== 1) { setSimActive(false); return; }
    const selItem = placed.find(p => p.uid === selected[0]);
    if (!selItem || !SIMULATABLE_LIFT_IDS.includes(selItem.id)) setSimActive(false);
  }, [selected, placed]);

  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      const r = entries[0].contentRect;
      const w = Math.round(r.width);
      const h = Math.round(r.height) - 42;
      // Guard against the classic ResizeObserver feedback loop: contentRect
      // is a float, and if measuring the container ever causes even a
      // sub-pixel change in its own size (a scrollbar flickering on/off
      // right at the threshold, layout rounding, etc.), calling setSize
      // with a functionally-identical value still creates a new object,
      // which re-renders, which re-measures — round-tripping forever and,
      // if the rounding has any directional bias, slowly drifting in one
      // direction over a long session instead of settling. This is almost
      // certainly the "canvas wandering and shrinking over time" report —
      // comparing against the last applied size and skipping true no-op
      // updates breaks the loop at the source instead of chasing symptoms.
      setSize(prev => (prev.w === w && prev.h === h ? prev : { w, h }));
    });
    if (areaRef.current) obs.observe(areaRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = e => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const zoomFactor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      const newZoom = Math.min(Math.max(zoomRef.current * zoomFactor, 0.3), 8);
      const curScale = baseScale * zoomRef.current;
      // Was shop.w/shop.h here — stale from before the free-form bbox-
      // centering fix. For rect shops shop.w/h already equal
      // shopBbox.w/h so this was invisible, but a free-form shop's saved
      // w/h isn't guaranteed to track its actual vertex bbox after
      // edits, so zoom-around-cursor could drift off from the point the
      // cursor is actually over. Matching the render-time formula
      // (which already uses shopBbox) keeps this consistent regardless.
      const curOx = baseOx + panStateRef.current.x + (shopBbox.w * baseScale / 2) * (1 - zoomRef.current);
      const curOy = baseOy + panStateRef.current.y + (shopBbox.h * baseScale / 2) * (1 - zoomRef.current);
      const shopX = (mx - curOx) / curScale;
      const shopY = (my - curOy) / curScale;
      const newScale = baseScale * newZoom;
      const newOxBase = baseOx + (shopBbox.w * baseScale / 2) * (1 - newZoom);
      const newOyBase = baseOy + (shopBbox.h * baseScale / 2) * (1 - newZoom);
      const newPanX = mx - shopX * newScale - newOxBase;
      const newPanY = my - shopY * newScale - newOyBase;
      zoomRef.current = newZoom;
      panStateRef.current = { x: newPanX, y: newPanY };
      setZoom(newZoom);
      setPan({ x: newPanX, y: newPanY });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [baseScale, baseOx, baseOy, shopBbox.w, shopBbox.h, svgRef]);

  useEffect(() => {
    const h = e => {
      const tag = document.activeElement?.tagName;
      if (['INPUT', 'TEXTAREA'].includes(tag)) return;

      // ─── Undo / Redo ─────────────────────────────────────────────
      // Ctrl+Z = undo, Ctrl+Y or Ctrl+Shift+Z = redo. Standard combos.
      // Hooked here (not inside the action handlers below) so an in-progress
      // dim drawing or other modal flow doesn't swallow them.
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
        e.preventDefault();
        undo && undo();
        // Clear any selection state that points at items that may no longer exist
        setSelected([]); setSelectedDrain(null); setSelectedDim(null);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) {
        e.preventDefault();
        redo && redo();
        setSelected([]); setSelectedDrain(null); setSelectedDim(null);
        return;
      }

      if (e.key === 'Escape') {
        // Free-form room drawing takes precedence: ESC cancels the in-progress polygon
        if (roomDrawModeRef.current) {
          if (setRoomDrawMode) setRoomDrawMode(false);
          return;
        }
        if (dimMode) { setDimMode(false); setDimStart(null); return; }
        setSelected([]); setSelectedDrain(null); setSelectedDim(null);
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        // During room drawing, Backspace removes the last placed vertex
        // (one-step undo for the current drawing). Delete also works.
        if (roomDrawModeRef.current) {
          const current = roomDrawVertsRef.current;
          if (current.length > 0) {
            const next = current.slice(0, -1);
            roomDrawVertsRef.current = next;
            setRoomDrawVerts(next);
          }
          e.preventDefault();
          return;
        }
        if (selectedDim) {
          setDimensions(prev => prev.filter(d => d.id !== selectedDim));
          setSelectedDim(null);
          commitHistory && commitHistory();
          return;
        }
        if (selected.length > 0) {
          // Lock-aware delete: locked items refuse to delete. If the entire
          // selection is locked, show a toast explaining why nothing happened.
          // If only some are locked, delete the unlocked ones and toast
          // about the skipped locked items.
          const selectedItems = placed.filter(x => selected.includes(x.uid));
          const lockedCount = selectedItems.filter(x => x.locked).length;
          const unlockedUids = selectedItems.filter(x => !x.locked).map(x => x.uid);
          if (unlockedUids.length === 0) {
            showToast && showToast(`Can't delete — ${lockedCount} item${lockedCount === 1 ? ' is' : 's are'} locked. Press L to unlock.`, 'error');
            return;
          }
          setPlaced(p => p.filter(x => !unlockedUids.includes(x.uid)));
          setSelected(prev => prev.filter(id => !unlockedUids.includes(id)));
          if (lockedCount > 0) {
            showToast && showToast(`Deleted ${unlockedUids.length}, kept ${lockedCount} locked item${lockedCount === 1 ? '' : 's'}.`, 'success');
          }
          commitHistory && commitHistory();
        } else if (selectedDrain) {
          setDrains(p => p.filter(x => x.id !== selectedDrain));
          setSelectedDrain(null);
          commitHistory && commitHistory();
        }
      }

      // ─── L = lock/unlock selected ────────────────────────────────
      // Toggles the lock flag on every selected item. If the selection is
      // mixed (some locked, some not), this locks them all (predictable
      // result rather than per-item flip which gets confusing).
      if (e.key === 'l' || e.key === 'L') {
        if (selected.length === 0) return;
        const selectedItems = placed.filter(x => selected.includes(x.uid));
        const anyUnlocked = selectedItems.some(x => !x.locked);
        const newLocked = anyUnlocked; // if any are unlocked, lock all; else unlock all
        setPlaced(p => p.map(x => selected.includes(x.uid) ? { ...x, locked: newLocked } : x));
        commitHistory && commitHistory();
        return;
      }

      if (e.key === '0') resetZoom();
      if (e.key === 'g' || e.key === 'G') setShowGrid(g => !g);
      // B = toggle bay clearance zones (only affects lifts with bayFootprint defined in catalog)
      if (e.key === 'b' || e.key === 'B') setShowBayZones(b => !b);
      // M = toggle exterior wall measurements + door offset labels.
      // Stored on shop (not local state) so the preference persists with save/load.
      // Skip when Ctrl/Cmd+M is pressed (browsers often bind it to minimize).
      if ((e.key === 'm' || e.key === 'M') && !e.ctrlKey && !e.metaKey) {
        if (typeof setShop === 'function') {
          setShop(s => ({ ...s, showWallMeasurements: !(s.showWallMeasurements !== false) }));
        }
      }
      // D = toggle door labels + handles (WLK/OVR text, width text, drag handle,
      // resize squares). Same persistence pattern as M. Skip Ctrl/Cmd+D since
      // browsers commonly bind that to bookmark.
      if ((e.key === 'd' || e.key === 'D') && !e.ctrlKey && !e.metaKey) {
        if (typeof setShop === 'function') {
          setShop(s => ({ ...s, showDoorLabels: !(s.showDoorLabels !== false) }));
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (selected.length > 0) {
          const items = placed.filter(p => selected.includes(p.uid));
          clipboardRef.current = items.map(i => JSON.parse(JSON.stringify(i)));
          // Copy doesn't change document state — no history commit needed
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (clipboardRef.current?.length > 0) {
          const newItems = clipboardRef.current.map(item => ({
            ...JSON.parse(JSON.stringify(item)),
            uid: `${Date.now()}-${Math.random()}`,
            cx: item.cx + 2, cy: item.cy + 2,
            _dragging: false,
            locked: false, // pasted copies start unlocked even if source was locked
          }));
          setPlaced(prev => [...prev, ...newItems]);
          setSelected(newItems.map(i => i.uid));
          setSelectedDrain(null);
          clipboardRef.current = newItems;
          commitHistory && commitHistory();
        }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [selected, selectedDrain, selectedDim, dimMode, placed, setPlaced, setSelected, setDrains, setSelectedDrain, setDimensions, setSelectedDim, commitHistory, undo, redo, showToast]);

  const doSnap = v => snapRef.current === 0 ? v : snap(v, snapRef.current);

  // useCallback here (not a plain function) so useImperativeHandle below can
  // list it as a dependency correctly — avoids an exhaustive-deps lint error
  // that CRA treats as a build-breaking error in Vercel's CI.
  const resetZoom = useCallback(() => {
    setZoom(1); setPan({ x: 0, y: 0 });
    zoomRef.current = 1; panStateRef.current = { x: 0, y: 0 };
  }, []);

  // Expose resetZoom to the parent component (App.js) via ref. This lets
  // App reset the view to defaults right before an export, so exports always
  // capture the full fitted/centered shop instead of whatever zoom/pan the
  // user happened to leave the editor at.
  // ─── Click-to-add (Sidebar "+" buttons) ─────────────────────────
  // Same result as dragging a catalog item onto the canvas, but dropped at
  // the centre of what's currently visible. Repeated clicks step each new
  // copy 2 ft down-right so they don't stack invisibly on top of each other.
  const addCatalogItem = (eqId) => {
    const { CATALOG } = require('../data/catalog');   // same lazy require as onDrop
    const eq = CATALOG.find(x => x.id === eqId);
    if (!eq) return null;
    const c = toShop(size.w / 2, size.h / 2, ox, oy, scale);
    let cx = doSnap(c.x), cy = doSnap(c.y);
    const occupied = (x, y) => placed.some(p => Math.abs(p.cx - x) < 0.5 && Math.abs(p.cy - y) < 0.5);
    for (let i = 0; i < 20 && occupied(cx, cy); i++) {
      cx += 2; cy += 2;
    }
    const newItem = {
      ...JSON.parse(JSON.stringify(eq)),
      uid: `${Date.now()}-${Math.random()}`,
      cx, cy, rot: 0, scaleX: 1, scaleY: 1, label: '',
    };
    setPlaced(prev => [...prev, newItem]);
    setSelected([newItem.uid]);
    setSelectedDrain(null); setSelectedDim(null);
    // Commit after React applies the add (see Sidebar's commitSoon note)
    setTimeout(() => commitHistory && commitHistory(), 0);
    showToast && showToast(`${eq.name} added — drag it into place.`, 'success');
    return newItem;
  };

  // Re-created each render so App's ref always calls the version that
  // sees the current zoom/pan (ox, oy, scale) and placed list
  useImperativeHandle(ref, () => ({ resetZoom, addCatalogItem }));

  // Overlapping items (real solid parts, ¼" tolerance) — drives the warning
  // badge in the bottom bar; the red outlines are drawn in drawAll.
  const overlapUids = useMemo(() => [...findOverlaps(placed).uids], [placed]);

  const zoomIn = () => {
    const newZoom = Math.min(8, zoomRef.current * 1.3);
    zoomRef.current = newZoom; setZoom(newZoom);
  };

  const zoomOut = () => {
    const newZoom = Math.max(0.3, zoomRef.current / 1.3);
    zoomRef.current = newZoom; setZoom(newZoom);
  };

  const calcDist = (x1, y1, x2, y2) =>
    Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

  // ─── Polygon room helpers ─────────────────────────────────────────
  // Build a polygon room from a list of vertices. Centroid is stored as
  // cx/cy so existing code that reads those (multi-select bbox calcs,
  // distribute math, etc.) keeps working. Vertices remain the source of truth
  // for rendering and dragging.
  const makePolygonRoom = (vertices, name = "Room") => {
    const c = polygonCentroid(vertices);
    const bb = computeBbox(vertices);
    return {
      uid: `${Date.now()}-${Math.random()}`,
      isRoom: true,
      shapeType: 'free',         // distinguishes polygon rooms from rect rooms
      id: "room",
      name,
      label: name,
      vertices: vertices.map(v => ({ x: v.x, y: v.y })),
      cx: c.x, cy: c.y,          // centroid — used by multi-select bbox/align logic
      fw: bb.w, fh: bb.h,        // bbox dims — used by sidebar size display
      scaleX: 1, scaleY: 1,
      rot: 0,
      color: "rgba(255,229,0,0.08)",
      border: "rgba(255,229,0,0.6)",
      _dragging: false,
    };
  };

  // Snap an in-progress drawing vertex to the start vertex (close detection).
  // Returns true if the cursor is within range of vertex[0] AND there are
  // already at least 2 vertices placed (so a triangle close is the minimum).
  // Threshold is in screen pixels (scaled to feet via current scale) so it
  // feels consistent at any zoom level. Default 12px.
  const isOverStartVertex = (shopX, shopY, verts, pxRadius = 12) => {
    if (!verts || verts.length < 3) return false;
    const v0 = verts[0];
    const ftRadius = pxRadius / scale;
    return calcDist(shopX, shopY, v0.x, v0.y) <= ftRadius;
  };

  // Translate all vertices of a polygon room by (dx, dy) for drag-to-move.
  // Centroid (cx, cy) and bbox (fw, fh) move with them; bbox dims don't change.
  const translatePolygonRoom = (room, dx, dy) => {
    if (!room.vertices) return room;
    const newVerts = room.vertices.map(v => ({ x: v.x + dx, y: v.y + dy }));
    return { ...room, vertices: newVerts, cx: room.cx + dx, cy: room.cy + dy };
  };

  // Ortho snap: if the line from (x1,y1) to (x2,y2) is close to horizontal,
  // force y2 = y1; if close to vertical, force x2 = x1. This gives the dim
  // line a clean 0°/90°/180°/270° look when the user is roughly aligned, while
  // still letting them draw at any angle if they aim further off-axis.
  // Returns the (possibly snapped) end point.
  const orthoSnap = (x1, y1, x2, y2) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    if (dx === 0 && dy === 0) return { x: x2, y: y2 };
    // Compare which axis dominates and how close the other axis is to zero.
    // A 1:5 ratio = ~11° off-axis, which feels like a comfortable snap window.
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    if (absDx > absDy * 5) return { x: x2, y: y1 };  // mostly horizontal -> snap Y
    if (absDy > absDx * 5) return { x: x1, y: y2 };  // mostly vertical -> snap X
    return { x: x2, y: y2 };
  };

  // Draws a dimension line with extension lines, label, and (when selected) drag handles.
  // Returns the group plus the perpendicular unit vector + midpoint so the caller can
  // wire up correct drag math regardless of line orientation.
  const drawDimLine = (svg, sx1, sy1, sx2, sy2, label, color, isSel, offset = -30) => {
    const dx = sx2 - sx1;
    const dy = sy2 - sy1;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 2) return null;
    // Perpendicular unit vector — this is the axis the offset extends along
    const nx = -dy / len;
    const ny = dx / len;
    // Offset line endpoints (the actual dimension bar shown to the user)
    const ox1 = sx1 + nx * offset;
    const oy1 = sy1 + ny * offset;
    const ox2 = sx2 + nx * offset;
    const oy2 = sy2 + ny * offset;
    const mx = (ox1 + ox2) / 2;
    const my = (oy1 + oy2) / 2;
    // Pick cursor based on perpendicular axis angle:
    // perpendicular near horizontal (|nx| > |ny|) -> drag left/right -> ew-resize
    // perpendicular near vertical                  -> drag up/down    -> ns-resize
    const cursor = Math.abs(nx) > Math.abs(ny) ? 'ew-resize' : 'ns-resize';
    const g = mk('g');
    // Extension lines from measurement points out to the offset bar
    g.appendChild(mk('line', { x1: sx1, y1: sy1, x2: ox1, y2: oy1, stroke: color, 'stroke-width': 0.6, 'stroke-dasharray': '3,2', opacity: 0.6 }));
    g.appendChild(mk('line', { x1: sx2, y1: sy2, x2: ox2, y2: oy2, stroke: color, 'stroke-width': 0.6, 'stroke-dasharray': '3,2', opacity: 0.6 }));
    // The visible dimension bar with arrowheads at both ends
    const ml = mk('line', { x1: ox1, y1: oy1, x2: ox2, y2: oy2, stroke: color, 'stroke-width': isSel ? 1.8 : 1.1, 'marker-start': 'url(#dimT)', 'marker-end': 'url(#dimH)' });
    g.appendChild(ml);
    // Big invisible hit strip along the dimension bar — makes clicking the line easy
    const hit = mk('line', { x1: ox1, y1: oy1, x2: ox2, y2: oy2, stroke: 'transparent', 'stroke-width': 24 });
    hit.style.cursor = cursor;
    g.appendChild(hit);
    // Label background + text (centered on the bar)
    const bgW = label.length * 5.5 + 10;
    g.appendChild(mk('rect', { x: mx - bgW / 2, y: my - 8, width: bgW, height: 14, fill: '#03080f', rx: 2, opacity: 0.9 }));
    const lbl = mkTxt(mx, my + 4, label, color, 8.5);
    lbl.setAttribute('pointer-events', 'none');
    g.appendChild(lbl);
    // Selected state: visible center handle + endpoint dots + invisible larger hit areas
    let centerHit = null;
    if (isSel) {
      const handle = mk('circle', { cx: mx, cy: my, r: 6, fill: color, opacity: 0.9 });
      handle.style.cursor = cursor;
      g.appendChild(handle);
      // Invisible hit circle at the center handle — much bigger than the visible dot
      // so users don't have to pixel-hunt to grab it
      centerHit = mk('circle', { cx: mx, cy: my, r: 16, fill: 'transparent' });
      centerHit.style.cursor = cursor;
      g.appendChild(centerHit);
      [{ x: sx1, y: sy1 }, { x: sx2, y: sy2 }].forEach(({ x, y }) => {
        g.appendChild(mk('circle', { cx: x, cy: y, r: 4, fill: color, opacity: 0.7 }));
      });
    }
    // Return everything callers need to drive the drag interaction correctly
    return { g, hit, centerHit, nx, ny, cursor };
  };

  const drawAll = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;
    // Bail out before paint if the canvas hasn't been measured yet.
    // size.h starts as -42 due to the toolbar offset (`r.height - 42`) when r.height is 0.
    // Without this guard, browsers log negative-dimension errors for SVG rects/patterns.
    if (size.w <= 0 || size.h <= 0) return;
    svg.innerHTML = '';
    svg.setAttribute('width', size.w);
    svg.setAttribute('height', size.h);

    const defs = mk('defs');
    const mH = mk('marker', { id: 'aH', markerWidth: 6, markerHeight: 6, refX: 6, refY: 3, orient: 'auto' });
    mH.appendChild(mk('path', { d: 'M0,0 L6,3 L0,6', fill: 'none', stroke: C.dim, 'stroke-width': 1 }));
    const mT = mk('marker', { id: 'aT', markerWidth: 6, markerHeight: 6, refX: 0, refY: 3, orient: 'auto' });
    mT.appendChild(mk('path', { d: 'M6,0 L0,3 L6,6', fill: 'none', stroke: C.dim, 'stroke-width': 1 }));
    const dmH = mk('marker', { id: 'dimH', markerWidth: 6, markerHeight: 6, refX: 6, refY: 3, orient: 'auto' });
    dmH.appendChild(mk('path', { d: 'M0,0 L6,3 L0,6', fill: 'none', stroke: C.measure, 'stroke-width': 1 }));
    const dmT = mk('marker', { id: 'dimT', markerWidth: 6, markerHeight: 6, refX: 0, refY: 3, orient: 'auto' });
    dmT.appendChild(mk('path', { d: 'M6,0 L0,3 L6,6', fill: 'none', stroke: C.measure, 'stroke-width': 1 }));
    defs.appendChild(mH); defs.appendChild(mT);
    defs.appendChild(dmH); defs.appendChild(dmT);
    // ─── Grid pattern definition ───
    // IMPORTANT: We draw BOTH minor and major grid lines inside a single pattern (`lg`).
    // Previously there was a nested pattern (lg containing a rect filled with `url(#sg)`)
    // which caused the minor grid to drift relative to the major grid during panning,
    // because nested patterns have their own coordinate space.
    // Single flat pattern = both grids stay perfectly aligned.
    const lg = mk('pattern', { id: 'lg', width: scale * 10, height: scale * 10, patternUnits: 'userSpaceOnUse', x: ox, y: oy });
    // Minor grid lines (every 2ft) — drawn first so major lines appear on top
    for (let i = 1; i < 5; i++) {
      const offset = i * scale * 2;
      // Vertical minor line
      lg.appendChild(mk('line', { x1: offset, y1: 0, x2: offset, y2: scale * 10, stroke: C.grid, 'stroke-width': 0.5 }));
      // Horizontal minor line
      lg.appendChild(mk('line', { x1: 0, y1: offset, x2: scale * 10, y2: offset, stroke: C.grid, 'stroke-width': 0.5 }));
    }
    // Major grid lines (every 10ft) — top and left edges of each tile
    lg.appendChild(mk('path', { d: `M ${scale * 10} 0 L 0 0 0 ${scale * 10}`, fill: 'none', stroke: C.gridMaj, 'stroke-width': 1 }));
    defs.appendChild(lg);

    // ─── Bay clearance hatch pattern ───
    // Diagonal stripes (like building-code "restricted area" hatching).
    // 8px tile, 45° lines via patternTransform — readable at any zoom level.
    // Amber tint = safety/clearance zone convention.
    const bayHatch = mk('pattern', {
      id: 'bayHatch', width: 8, height: 8, patternUnits: 'userSpaceOnUse',
      patternTransform: 'rotate(45)',
    });
    bayHatch.appendChild(mk('rect', { x: 0, y: 0, width: 8, height: 8, fill: 'rgba(255,170,40,0.08)' }));
    bayHatch.appendChild(mk('line', { x1: 0, y1: 0, x2: 0, y2: 8, stroke: '#ffaa28', 'stroke-width': 1.2, opacity: 0.45 }));
    defs.appendChild(bayHatch);

    svg.appendChild(defs);

    // ─── Background fill (full viewport) ───
    svg.appendChild(mk('rect', { x: 0, y: 0, width: size.w, height: size.h, fill: C.bg }));

    // ─── Single grid layer covering the full viewport ───
    // One rect, one pattern, no inside/outside distinction.
    // Pattern is anchored at (ox, oy) so it pans correctly with the shop.
    if (showGrid) {
      svg.appendChild(mk('rect', {
        x: 0, y: 0, width: size.w, height: size.h,
        fill: 'url(#lg)', opacity: 0.9,
      }));
    }

    // ─── Shop floor outline tint (subtle, drawn ON TOP of grid) ───
    // Slightly tints the inside of the shop so users can see the working area.
    // For polygon shops uses the actual vertex outline; rectangles also work
    // since they have a 4-vertex polygon stored on shop.vertices.
    {
      const verts = shop.vertices && shop.vertices.length >= 3
        ? shop.vertices.map(v => `${ox + v.x * scale},${oy + v.y * scale}`).join(' ')
        : null;
      if (verts) {
        svg.appendChild(mk('polygon', {
          points: verts,
          fill: 'rgba(3, 12, 24, 0.35)',
        }));
      } else {
        // Fallback for malformed shop data — shouldn't normally happen post-migration
        svg.appendChild(mk('rect', {
          x: ox, y: oy, width: shop.w * scale, height: shop.h * scale,
          fill: 'rgba(3, 12, 24, 0.35)',
        }));
      }
    }

    // ─── Crosshairs follow mouse ───
    svg.appendChild(mk('line', { x1: mouse.x, y1: 0, x2: mouse.x, y2: size.h, stroke: C.cross, 'stroke-width': 1 }));
    svg.appendChild(mk('line', { x1: 0, y1: mouse.y, x2: size.w, y2: mouse.y, stroke: C.cross, 'stroke-width': 1 }));

    // ROOMS — branches on item.shapeType. Rectangle rooms keep the legacy
    // path (cx/cy + fw/fh + scale + rot). Polygon rooms render as an SVG
    // polygon traced from item.vertices in shop coordinates.
    placed.filter(item => item.isRoom).forEach(item => {
      const isSel = selected.includes(item.uid);
      const isLocked = !!item.locked;
      const isPolygon = item.shapeType === 'free' && Array.isArray(item.vertices) && item.vertices.length >= 3;

      // ─── POLYGON ROOM ────────────────────────────────────────────
      if (isPolygon) {
        const g = mk('g');
        g.style.cursor = isLocked ? 'not-allowed' : 'grab';
        const pts = item.vertices.map(v => `${ox + v.x * scale},${oy + v.y * scale}`).join(' ');
        // Main polygon fill + dashed border (matches rect room visual language)
        g.appendChild(mk('polygon', {
          points: pts,
          fill: item.color || 'rgba(255,229,0,0.08)',
          stroke: isSel ? C.accent : (item.border || 'rgba(255,229,0,0.6)'),
          'stroke-width': isSel ? 1.5 : 1,
          'stroke-dasharray': '6,3',
        }));
        // Label at centroid (cx/cy)
        const cpx = ox + item.cx * scale;
        const cpy = oy + item.cy * scale;
        const bbW = item.fw * scale;   // bbox width in pixels, for label size hint
        const lblFont = Math.min(13, Math.max(9, bbW * 0.08));
        const lbl = mkTxt(cpx, cpy, item.label || item.name || 'Room', '#ffffff', lblFont);
        lbl.setAttribute('font-weight', '600');
        lbl.setAttribute('pointer-events', 'none');
        g.appendChild(lbl);
        // Selection ring as a bbox rectangle around the polygon (simpler than
        // tracing an offset polygon; users still see selected state clearly)
        if (isSel) {
          const bb = computeBbox(item.vertices);
          g.appendChild(mk('rect', {
            x: ox + bb.minX * scale - 4, y: oy + bb.minY * scale - 4,
            width: bb.w * scale + 8, height: bb.h * scale + 8,
            fill: 'none', stroke: C.accent,
            'stroke-width': isLocked ? 1.2 : 1.5,
            'stroke-dasharray': isLocked ? '2,3' : '4,3',
            rx: 3, opacity: isLocked ? 0.7 : 0.9,
          }));
        }
        // Lock badge at the polygon's bbox top-left
        if (isLocked && !hideChrome) {
          const bb = computeBbox(item.vertices);
          drawLockBadge(g, ox + bb.minX * scale + 6, oy + bb.minY * scale + 6, 0);
        }
        // Mousedown — same select/multi-move logic as rect rooms; the move
        // handler translates vertices for polygon rooms automatically.
        g.addEventListener('mousedown', e => {
          if (dimModeRef.current || roomDrawModeRef.current) return;
          if (interactRef.current) return;
          e.stopPropagation(); didDragRef.current = false;
          const rect = svgRef.current.getBoundingClientRect();
          const sc = toShop(e.clientX - rect.left, e.clientY - rect.top, ox, oy, scale);
          if (e.shiftKey) {
            setSelected(prev => prev.includes(item.uid) ? prev.filter(id => id !== item.uid) : [...prev, item.uid]);
            return;
          }
          if (isLocked) {
            setSelected([item.uid]); setSelectedDrain(null); setSelectedDim(null);
            return;
          }
          if (selected.includes(item.uid) && selected.length > 1) {
            const movableStarts = placed
              .filter(p => selected.includes(p.uid) && !p.locked)
              .map(p => ({
                uid: p.uid, cx: p.cx, cy: p.cy,
                vertices: p.vertices ? p.vertices.map(v => ({ x: v.x, y: v.y })) : null,
              }));
            interactRef.current = {
              mode: 'moveMulti', startMx: sc.x, startMy: sc.y,
              starts: movableStarts,
            };
            return;
          }
          interactRef.current = { mode: 'move', uid: item.uid, offX: sc.x - item.cx, offY: sc.y - item.cy };
          setSelected([item.uid]); setSelectedDrain(null); setSelectedDim(null);
        });
        g.addEventListener('click', e => {
          if (dimModeRef.current || roomDrawModeRef.current) return;
          e.stopPropagation();
          if (!didDragRef.current && !e.shiftKey) {
            setSelected([item.uid]); setSelectedDrain(null); setSelectedDim(null);
          }
        });
        svg.appendChild(g);
        return;  // skip rect-room rendering below
      }

      // ─── RECTANGLE ROOM (legacy path) ──────────────────────────────
      const cpx = ox + item.cx * scale;
      const cpy = oy + item.cy * scale;
      const pw = item.fw * item.scaleX * scale;
      const ph = item.fh * item.scaleY * scale;
      const g = mk('g');
      g.setAttribute('transform', `translate(${cpx},${cpy}) rotate(${item.rot || 0})`);
      // Locked items show a not-allowed cursor as a hint that they won't drag
      g.style.cursor = isLocked ? 'not-allowed' : 'grab';
      g.appendChild(mk('rect', {
        x: -pw / 2, y: -ph / 2, width: pw, height: ph,
        fill: item.color || 'rgba(255,229,0,0.08)',
        stroke: isSel ? C.accent : (item.border || 'rgba(255,229,0,0.6)'),
        'stroke-width': isSel ? 1.5 : 1, 'stroke-dasharray': '6,3', rx: 2,
      }));
      const lbl = mkTxt(0, 0, item.label || item.name || 'Room',
        '#ffffff',
        Math.min(13, Math.max(9, pw * 0.09)));
      lbl.setAttribute('font-weight', '600');
      lbl.setAttribute('pointer-events', 'none');
      lbl.setAttribute('transform', `rotate(${-(item.rot || 0)})`);
      g.appendChild(lbl);
      if (pw > 60 && ph > 40) {
        const szTxt = mkTxt(0, 14,
          `${(item.fw * item.scaleX).toFixed(0)}x${(item.fh * item.scaleY).toFixed(0)} ${shop.unit}`,
          '#aaccee', 8.5);
        szTxt.setAttribute('pointer-events', 'none');
        szTxt.setAttribute('transform', `rotate(${-(item.rot || 0)})`);
        g.appendChild(szTxt);
      }
      if (isSel && !isLocked) {
        g.appendChild(mk('rect', {
          x: -pw / 2 - 4, y: -ph / 2 - 4, width: pw + 8, height: ph + 8,
          fill: 'none', stroke: C.accent, 'stroke-width': 1.5,
          'stroke-dasharray': '4,3', rx: 3, opacity: 0.9,
        }));
        [{ name: 'NW', cx: -pw / 2, cy: -ph / 2, cur: 'nwse-resize' },
          { name: 'NE', cx: pw / 2, cy: -ph / 2, cur: 'nesw-resize' },
          { name: 'SW', cx: -pw / 2, cy: ph / 2, cur: 'nesw-resize' },
          { name: 'SE', cx: pw / 2, cy: ph / 2, cur: 'nwse-resize' },
        ].forEach(({ name, cx: hx, cy: hy, cur }) => {
          const hg = mk('g'); hg.style.cursor = cur;
          hg.appendChild(mk('circle', { cx: hx, cy: hy, r: 8, fill: '#030912', stroke: 'rgba(255,229,0,0.8)', 'stroke-width': 1.5, opacity: 0.9 }));
          hg.appendChild(mk('circle', { cx: hx, cy: hy, r: 3, fill: 'rgba(255,229,0,0.8)', opacity: 0.85 }));
          const hit = mk('circle', { cx: hx, cy: hy, r: 14, fill: 'transparent' });
          hit.addEventListener('mousedown', ev => { ev.stopPropagation(); interactRef.current = { mode: `scale${name}`, uid: item.uid }; });
          hg.appendChild(hit); g.appendChild(hg);
        });
      }
      // Selection ring for locked rooms (no resize handles, just an outline)
      if (isSel && isLocked) {
        g.appendChild(mk('rect', {
          x: -pw / 2 - 4, y: -ph / 2 - 4, width: pw + 8, height: ph + 8,
          fill: 'none', stroke: C.accent, 'stroke-width': 1.2,
          'stroke-dasharray': '2,3', rx: 3, opacity: 0.7,
        }));
      }
      // Lock badge — small padlock indicator at top-left corner of locked items
      if (isLocked && !hideChrome) drawLockBadge(g, -pw / 2 + 6, -ph / 2 + 6, item.rot || 0);
      g.addEventListener('mousedown', e => {
        if (dimModeRef.current || roomDrawModeRef.current) return;
        if (interactRef.current) return;
        e.stopPropagation(); didDragRef.current = false;
        const rect = svgRef.current.getBoundingClientRect();
        const sc = toShop(e.clientX - rect.left, e.clientY - rect.top, ox, oy, scale);
        if (e.shiftKey) {
          setSelected(prev => prev.includes(item.uid) ? prev.filter(id => id !== item.uid) : [...prev, item.uid]);
          return;
        }
        // Locked items can be selected (so user can press L to unlock) but
        // never enter a move/multi-move interaction.
        if (isLocked) {
          setSelected([item.uid]); setSelectedDrain(null); setSelectedDim(null);
          return;
        }
        if (selected.includes(item.uid) && selected.length > 1) {
          // Multi-move: only the unlocked items in the selection actually move.
          // We capture starts only for unlocked uids.
          const movableStarts = placed
            .filter(p => selected.includes(p.uid) && !p.locked)
            .map(p => ({
              uid: p.uid, cx: p.cx, cy: p.cy,
              // Polygon rooms need their original vertex positions snapshotted
              // so multi-move can translate them by (dx, dy) without drift.
              vertices: p.vertices ? p.vertices.map(v => ({ x: v.x, y: v.y })) : null,
            }));
          interactRef.current = {
            mode: 'moveMulti', startMx: sc.x, startMy: sc.y,
            starts: movableStarts,
          };
          return;
        }
        interactRef.current = { mode: 'move', uid: item.uid, offX: sc.x - item.cx, offY: sc.y - item.cy };
        setSelected([item.uid]); setSelectedDrain(null); setSelectedDim(null);
      });
      g.addEventListener('click', e => {
        if (dimModeRef.current || roomDrawModeRef.current) return;          // Don't consume clicks during measure
        e.stopPropagation();
        if (!didDragRef.current && !e.shiftKey) { setSelected([item.uid]); setSelectedDrain(null); setSelectedDim(null); }
      });
      svg.appendChild(g);
    });

    // FLOOR DRAINS
    drains.forEach(drain => {
      const isSel = drain.id === selectedDrain;
      const dpx = ox + drain.cx * scale;
      const dpy = oy + drain.cy * scale;
      const dw = drain.w * scale;
      const dh = drain.h * scale;
      const g = mk('g');
      g.setAttribute('transform', `translate(${dpx},${dpy}) rotate(${drain.rot || 0})`);
      g.style.cursor = 'grab';
      g.appendChild(mk('rect', { x: -dw / 2, y: -dh / 2, width: dw, height: dh, fill: C.drainFill, stroke: isSel ? C.accent : C.drain, 'stroke-width': isSel ? 1.5 : 1, 'stroke-dasharray': '4,2', rx: 1 }));
      g.appendChild(mk('line', { x1: -dw / 2, y1: 0, x2: dw / 2, y2: 0, stroke: C.drain, 'stroke-width': 0.6, opacity: 0.6 }));
      g.appendChild(mk('line', { x1: 0, y1: -dh / 2, x2: 0, y2: dh / 2, stroke: C.drain, 'stroke-width': 0.6, opacity: 0.6 }));
      const lbl = mkTxt(0, dh / 2 + 10, drain.label || 'Floor Drain', C.drain, 7.5);
      lbl.setAttribute('pointer-events', 'none'); g.appendChild(lbl);
      if (dw > 30 && dh > 20) {
        const sz = mkTxt(0, 3.5, `${drain.w}x${drain.h}`, C.drain, 7);
        sz.setAttribute('opacity', 0.7); sz.setAttribute('pointer-events', 'none'); g.appendChild(sz);
      }
      if (isSel) {
        g.appendChild(mk('rect', { x: -dw / 2 - 4, y: -dh / 2 - 4, width: dw + 8, height: dh + 8, fill: 'none', stroke: C.accent, 'stroke-width': 1.2, 'stroke-dasharray': '4,3', rx: 2 }));
        [[-dw / 2, -dh / 2], [dw / 2, -dh / 2], [-dw / 2, dh / 2], [dw / 2, dh / 2]].forEach(([hx, hy]) => {
          const h = mk('circle', { cx: hx, cy: hy, r: 5, fill: C.drain, stroke: '#040b14', 'stroke-width': 1, style: 'cursor:nwse-resize;' });
          h.addEventListener('mousedown', ev => { ev.stopPropagation(); drainDragRef.current = { mode: 'scale', id: drain.id }; });
          g.appendChild(h);
        });
      }
      g.addEventListener('mousedown', e => {
        if (dimModeRef.current) return;
        if (drainDragRef.current) return;
        e.stopPropagation(); didDragRef.current = false;
        const rect = svgRef.current.getBoundingClientRect();
        const sc = toShop(e.clientX - rect.left, e.clientY - rect.top, ox, oy, scale);
        drainDragRef.current = { mode: 'move', id: drain.id, offX: sc.x - drain.cx, offY: sc.y - drain.cy };
        setSelectedDrain(drain.id); setSelected([]); setSelectedDim(null);
      });
      g.addEventListener('click', e => {
        if (dimModeRef.current) return;          // Don't consume clicks during measure
        e.stopPropagation();
        if (!didDragRef.current) { setSelectedDrain(drain.id); setSelected([]); setSelectedDim(null); }
      });
      svg.appendChild(g);
    });

    // EQUIPMENT
    // Items whose real solid parts overlap (see collision.findOverlaps) get a
    // red dashed outline. Touching / very close is fine — only true overlap flags.
    const overlapping = hideChrome ? new Set() : findOverlaps(placed).uids;
    placed.filter(item => !item.isRoom).forEach(item => {
      const isSel = selected.includes(item.uid);
      const isLocked = !!item.locked;
      const cpx = ox + item.cx * scale;
      const cpy = oy + item.cy * scale;
      const pw = item.fw * item.scaleX * scale;
      const ph = item.fh * item.scaleY * scale;
      const hasImage = !!EQ_IMAGES[item.id];
      const isObstruction = item.isObstruction;
      const g = mk('g');
      g.setAttribute('transform', `translate(${cpx},${cpy}) rotate(${item.rot})`);
      // Locked items show a not-allowed cursor as a hint they won't drag
      g.style.cursor = dimMode ? 'crosshair' : (isLocked ? 'not-allowed' : 'grab');
      if (isSel) {
        g.appendChild(mk('rect', { x: -pw / 2 - 4, y: -ph / 2 - 4, width: pw + 8, height: ph + 8, fill: 'none', stroke: C.accent, 'stroke-width': 1.5, 'stroke-dasharray': '4,3', rx: 2, opacity: 0.9 }));
      }

      // ─── Bay clearance zone overlay ───
      // Drawn BEFORE the lift body so the equipment image sits visually on top.
      // Lives inside the rotated <g>, so the zone spins with the lift automatically.
      // Visibility: per-lift override wins if set, otherwise follow global toggle.
      // Only renders if the catalog defines a bayFootprint for this lift type.
      //
      // LEGACY FALLBACK: items saved before bayFootprint existed won't have the
      // field on the item itself. Look it up from the current CATALOG by id so
      // pre-existing JSON projects show zones too. Lazy-require matches the
      // pattern used in onDrop below (avoids any circular import risk).
      const catalogEntry = !item.bayFootprint
        ? require('../data/catalog').CATALOG.find(c => c.id === item.id)
        : null;
      const footprint = item.bayFootprint || (catalogEntry && catalogEntry.bayFootprint);
      if (footprint) {
        const perLift = item.showBayZone;       // true / false / undefined
        const shouldShow = perLift === undefined ? showBayZones : perLift;
        if (shouldShow) {
          // Convert footprint feet → pixels. width = side-to-side, depth = fore/aft.
          // NOT scaled by item.scaleX/scaleY — clearance is a fixed real-world spec,
          // not something that grows when the user resizes the lift visual.
          const bw = footprint.width * scale;
          const bd = footprint.depth * scale;
          g.appendChild(mk('rect', {
            x: -bw / 2, y: -bd / 2, width: bw, height: bd,
            fill: 'url(#bayHatch)',
            stroke: '#ffaa28', 'stroke-width': 1,
            'stroke-dasharray': '6,4', opacity: 0.9,
            'pointer-events': 'none',         // Don't intercept clicks meant for the lift
            rx: 1,
          }));
        }
      }

      if (hasImage) {
        const clipId = `clip-${item.uid}`;
        const clipPath = mk('clipPath', { id: clipId });
        clipPath.appendChild(mk('rect', { x: -pw / 2, y: -ph / 2, width: pw, height: ph }));
        svg.querySelector('defs').appendChild(clipPath);
        g.appendChild(mk('image', { href: EQ_IMAGES[item.id], x: -pw / 2, y: -ph / 2, width: pw, height: ph, preserveAspectRatio: 'xMidYMid meet', opacity: item._dragging ? 0.5 : 1, 'clip-path': `url(#${clipId})` }));
        if (isSel) g.appendChild(mk('rect', { x: -pw / 2, y: -ph / 2, width: pw, height: ph, fill: 'none', stroke: C.accent, 'stroke-width': 1, rx: 1 }));
       const lbl = mkTxt(0, ph / 2 + 13, item.label || item.name.toUpperCase(), '#ffffff', Math.min(9, Math.max(7.5, pw * 0.12)));
        lbl.setAttribute('pointer-events', 'none');
        lbl.setAttribute('transform', `rotate(${-item.rot})`);
        g.appendChild(lbl);
      } else if (isObstruction) {
        g.appendChild(mk('rect', { x: -pw / 2, y: -ph / 2, width: pw, height: ph, fill: '#1a0a0a', stroke: '#cc3333', 'stroke-width': isSel ? 2 : 1.2, rx: 1 }));
        g.appendChild(mk('line', { x1: -pw / 2, y1: -ph / 2, x2: pw / 2, y2: ph / 2, stroke: '#cc3333', 'stroke-width': 1, opacity: 0.5 }));
        g.appendChild(mk('line', { x1: pw / 2, y1: -ph / 2, x2: -pw / 2, y2: ph / 2, stroke: '#cc3333', 'stroke-width': 1, opacity: 0.5 }));
        if (pw > 24) {
          const lbl = mkTxt(0, 3, item.label || 'OBSTRUCTION', '#ff6666', Math.min(9, pw * 0.22));
          lbl.setAttribute('pointer-events', 'none');
          lbl.setAttribute('transform', `rotate(${-item.rot})`);
          g.appendChild(lbl);
        }
      } else {
        g.appendChild(mk('rect', { x: -pw / 2 + 2, y: -ph / 2 + 2, width: pw, height: ph, fill: 'rgba(0,0,0,0.4)', rx: 1 }));
        g.appendChild(mk('rect', { x: -pw / 2, y: -ph / 2, width: pw, height: ph, fill: item.color, stroke: item.border, 'stroke-width': isSel ? 1.5 : 0.8, rx: 1, opacity: item._dragging ? 0.5 : 1 }));
        if (pw > 16 && ph > 16) [0, 1, 2, 3].forEach(i => {
          g.appendChild(mk('line', { x1: -pw / 2 + pw * 0.25 * i, y1: -ph / 2, x2: -pw / 2, y2: -ph / 2 + ph * 0.25 * i, stroke: item.border, 'stroke-width': 0.35, opacity: 0.2 }));
        });
        if (pw > 22 && ph > 22) {
          [[-5, 0, 5, 0], [0, -5, 0, 5]].forEach(([x1, y1, x2, y2]) => {
            g.appendChild(mk('line', { x1, y1, x2, y2, stroke: item.border, 'stroke-width': 0.6, opacity: 0.4 }));
          });
        }
        if (pw > 36 && ph > 22) {
          const lblG = mk('g');
          lblG.setAttribute('transform', `rotate(${-item.rot})`);
          lblG.setAttribute('pointer-events', 'none');
          if (item.label) {
           lblG.appendChild(mkTxt(0, ph > 50 ? -14 : -4, item.label, '#ffffff', Math.min(11, pw * 0.2)));
            const t2 = mkTxt(0, ph > 50 ? -2 : 7, item.name.toUpperCase(), '#ffffff', Math.min(9, pw * 0.17));
            t2.setAttribute('opacity', 0.7); lblG.appendChild(t2);
          } else {
          lblG.appendChild(mkTxt(0, ph > 50 ? -6 : 3, item.name.toUpperCase(), '#ffffff', Math.min(10, pw * 0.2)));
          }
          if (ph > 50) {
            const t3 = mkTxt(0, 10, `${(item.fw * item.scaleX).toFixed(1)}x${(item.fh * item.scaleY).toFixed(1)} ${shop.unit}`, '#aaccee', Math.min(8, pw * 0.15));
            t3.setAttribute('opacity', 0.85); lblG.appendChild(t3);
          }
          g.appendChild(lblG);
        }
      }
      [[-pw / 2, -ph / 2], [pw / 2, -ph / 2], [-pw / 2, ph / 2], [pw / 2, ph / 2]].forEach(([cx, cy]) => {
        g.appendChild(mk('circle', { cx, cy, r: 1.5, fill: item.border, opacity: 0.5 }));
      });
      if (isSel && selected.length === 1 && !dimMode && !isLocked) {
        const rotR = Math.max(pw, ph) / 2 + 24;
        const arcG = mk('g'); arcG.style.cursor = 'grab';
        arcG.appendChild(mk('path', { d: `M ${-rotR * 0.55},${-rotR} A ${rotR} ${rotR} 0 0 1 ${rotR * 0.55},${-rotR}`, fill: 'none', stroke: C.accent, 'stroke-width': 2, 'stroke-dasharray': '4,2', 'stroke-linecap': 'round' }));
        arcG.appendChild(mk('circle', { cx: rotR * 0.55, cy: -rotR, r: 5, fill: C.accent, opacity: 0.9 }));
        const arcHit = mk('path', { d: `M ${-rotR * 0.75},${-rotR + 12} A ${rotR} ${rotR} 0 0 1 ${rotR * 0.75},${-rotR + 12}`, fill: 'none', stroke: 'transparent', 'stroke-width': 20 });
        arcHit.style.cursor = 'grab';
        arcHit.addEventListener('mousedown', e => { e.stopPropagation(); interactRef.current = { mode: 'rotate', uid: item.uid }; });
        arcG.appendChild(arcHit); g.appendChild(arcG);
        const rotLbl = mkTxt(0, -rotR - 9, `${Math.round(item.rot)}°`, C.accent, 8);
        rotLbl.setAttribute('pointer-events', 'none'); g.appendChild(rotLbl);
        [{ name: 'NW', cx: -pw / 2, cy: -ph / 2, cur: 'nwse-resize' },
          { name: 'NE', cx: pw / 2, cy: -ph / 2, cur: 'nesw-resize' },
          { name: 'SW', cx: -pw / 2, cy: ph / 2, cur: 'nesw-resize' },
          { name: 'SE', cx: pw / 2, cy: ph / 2, cur: 'nwse-resize' },
        ].forEach(({ name, cx: hx, cy: hy, cur }) => {
          const hg = mk('g'); hg.style.cursor = cur;
          hg.appendChild(mk('circle', { cx: hx, cy: hy, r: 10, fill: '#030912', stroke: C.dim, 'stroke-width': 1.5, opacity: 0.9 }));
          hg.appendChild(mk('circle', { cx: hx, cy: hy, r: 3.5, fill: C.dim, opacity: 0.85 }));
          const lbl = mkTxt(hx, hy + 3.5, name, C.dim, 6);
          lbl.setAttribute('pointer-events', 'none'); hg.appendChild(lbl);
          const hit = mk('circle', { cx: hx, cy: hy, r: 16, fill: 'transparent' });
          hit.addEventListener('mousedown', ev => { ev.stopPropagation(); interactRef.current = { mode: `scale${name}`, uid: item.uid }; });
          hg.appendChild(hit); g.appendChild(hg);
        });
      }
      // Lock badge — small padlock indicator at top-left corner of locked items
      if (isLocked && !hideChrome) drawLockBadge(g, -pw / 2 + 6, -ph / 2 + 6, item.rot || 0);
      // Overlap warning outline
      if (overlapping.has(item.uid)) {
        g.appendChild(mk('rect', {
          x: -pw / 2 - 2, y: -ph / 2 - 2, width: pw + 4, height: ph + 4, rx: 2,
          fill: 'rgba(255,77,77,0.08)', stroke: '#ff4d4d', 'stroke-width': 1.5,
          'stroke-dasharray': '5,3', 'pointer-events': 'none',
        }));
      }
      g.addEventListener('mousedown', e => {
        if (dimModeRef.current) return;
        if (interactRef.current) return;
        e.stopPropagation(); didDragRef.current = false;
        const rect = svgRef.current.getBoundingClientRect();
        const sc = toShop(e.clientX - rect.left, e.clientY - rect.top, ox, oy, scale);
        if (e.shiftKey) {
          setSelected(prev => prev.includes(item.uid) ? prev.filter(id => id !== item.uid) : [...prev, item.uid]);
          return;
        }
        // Locked items can be selected (so user can press L to unlock) but
        // never enter a move/multi-move interaction.
        if (isLocked) {
          setSelected([item.uid]); setSelectedDrain(null); setSelectedDim(null);
          return;
        }
        if (selected.includes(item.uid) && selected.length > 1) {
          // Multi-move: only the unlocked items in the selection actually move.
          const movableStarts = placed
            .filter(p => selected.includes(p.uid) && !p.locked)
            .map(p => ({
              uid: p.uid, cx: p.cx, cy: p.cy,
              // Polygon rooms need their original vertex positions snapshotted
              // so multi-move can translate them by (dx, dy) without drift.
              vertices: p.vertices ? p.vertices.map(v => ({ x: v.x, y: v.y })) : null,
            }));
          interactRef.current = {
            mode: 'moveMulti', startMx: sc.x, startMy: sc.y,
            starts: movableStarts,
          };
          return;
        }
        interactRef.current = { mode: 'move', uid: item.uid, offX: sc.x - item.cx, offY: sc.y - item.cy, startCx: item.cx, startCy: item.cy };
        setSelected([item.uid]); setSelectedDrain(null); setSelectedDim(null);
      });
      g.addEventListener('click', e => {
        if (dimModeRef.current) return;          // Don't consume clicks during measure
        e.stopPropagation();
        if (!didDragRef.current && !e.shiftKey) { setSelected([item.uid]); setSelectedDrain(null); setSelectedDim(null); }
      });
      svg.appendChild(g);
    });

    // WALLS — drawn from shop.vertices so both rectangle and free-form shops
    // use the same path. Falls back to the legacy rect if vertices are missing
    // (defensive — migrateShop should have populated them).
    {
      const verts = shop.vertices && shop.vertices.length >= 3
        ? shop.vertices.map(v => `${ox + v.x * scale},${oy + v.y * scale}`).join(' ')
        : null;
      if (verts) {
        svg.appendChild(mk('polygon', {
          points: verts,
          fill: 'none',
          stroke: C.wall,
          'stroke-width': 2.5,
          filter: `drop-shadow(0 0 5px ${C.wallGlow})`,
        }));
      } else {
        svg.appendChild(mk('rect', { x: ox, y: oy, width: shop.w * scale, height: shop.h * scale, fill: 'none', stroke: C.wall, 'stroke-width': 2.5, filter: `drop-shadow(0 0 5px ${C.wallGlow})` }));
      }
    }

    // DOORS — unified rendering using wall-attached door format.
    // Each door has { wallId, offset, width, type } and getDoorEndpoints()
    // returns the start/end/midpoint in shop coords plus the wall's unit and
    // perpendicular vectors. This works for both axis-aligned (rectangle) walls
    // and arbitrary-angle (free-form) walls.
    //
    // isFreeForm is computed up here (rather than just before the dimension lines
    // section below) because the door rendering needs it for wallLabelClearance.
    const isFreeForm = shop.shapeType === 'free';
    doors.forEach(d => {
      const ends = getDoorEndpoints(d, shop);
      if (!ends) return; // door references a wall that no longer exists — skip

      const doorFt = d.width || (d.type === 'overhead' ? 10 : 4);
      const clr = d.type === 'overhead' ? C.overhead : C.door;

      // Convert shop coords to canvas pixels
      const pxStart = { x: ox + ends.start.x * scale, y: oy + ends.start.y * scale };
      const pxEnd   = { x: ox + ends.end.x * scale,   y: oy + ends.end.y * scale };
      const pxMid   = { x: ox + ends.midpoint.x * scale, y: oy + ends.midpoint.y * scale };

      // Outward direction (away from polygon interior). getDoorEndpoints
      // computes this via point-in-polygon test, so it's correct for both
      // clockwise (rectangles, drawn-clockwise free-form) and counterclockwise
      // (drawn-counterclockwise free-form) polygons.
      const outX = ends.outwardVec.x;
      const outY = ends.outwardVec.y;

      // Determine if the wall is more horizontal than vertical — used to pick
      // a sensible cursor for the width-resize handles.
      const isMostlyHorizontal = Math.abs(ends.unitVec.x) >= Math.abs(ends.unitVec.y);

      // ─── Door opening line (knock out wall, then color) ───
      svg.appendChild(mk('line', {
        x1: pxStart.x, y1: pxStart.y, x2: pxEnd.x, y2: pxEnd.y,
        stroke: C.bg, 'stroke-width': 6,
      }));
      const dl = mk('line', {
        x1: pxStart.x, y1: pxStart.y, x2: pxEnd.x, y2: pxEnd.y,
        stroke: clr, 'stroke-width': 3,
      });
      dl.setAttribute('filter', `drop-shadow(0 0 3px ${clr}88)`);
      svg.appendChild(dl);

      // ─── Stack labels + handle outward from door midpoint ───
      // Layered: type label (close), width label (if overhead), drag handle, position label.
      // Each is placed by walking outward along (outX, outY) from the midpoint.
      //
      // In free-form mode the wall length label sits ~1.8 ft outside each wall
      // (= 1.8 * scale px). Door labels need to start BEYOND that to avoid
      // overlap, so we add an extra offset in free-form mode.
      //
      // Spacing between labels needs to be generous because on vertical walls
      // the labels stack horizontally (text width is ~40-50px), not vertically.
      // 16px between type and width keeps them clear in either orientation.
      const wallLabelClearance = isFreeForm ? Math.max(24, 1.8 * scale + 14) : 0;
      const labelTypeOff   = 8  + wallLabelClearance;
      const labelWidthOff  = 24 + wallLabelClearance;
      const handleOff      = 46 + wallLabelClearance;
      const labelPosOff    = 60 + wallLabelClearance;

      // Door-text + handles visibility. Three conditions hide them:
      //   1. Export mode (hideChrome=true) — these are editing UI, not for printed plans
      //   2. User toggled them off via the toolbar (shop.showDoorLabels === false)
      //   3. Defaults to ON: undefined or true both show them
      // The door LINE itself always renders — only the text + handles can hide.
      const showDoorChrome = !hideChrome && (shop.showDoorLabels !== false);

      if (showDoorChrome) {
        svg.appendChild(mkTxt(
          pxMid.x + outX * labelTypeOff,
          pxMid.y + outY * labelTypeOff,
          d.type === 'overhead' ? 'OVR' : 'WLK',
          clr, 7, 'middle'
        ));
        if (d.type === 'overhead') {
          svg.appendChild(mkTxt(
            pxMid.x + outX * labelWidthOff,
            pxMid.y + outY * labelWidthOff,
            `${doorFt}ft wide`,
            clr, 7, 'middle'
          ));
        }
      }

      // ─── Drag handle (move the whole door along its wall) ───
      // Small visible dot (r=6) with a generous invisible hit area (r=18)
      // underneath. The small visual footprint stops handles from dominating
      // the canvas on large shops, while the wider hit zone keeps them just
      // as easy to grab as before.
      //
      // Hidden when showDoorChrome is false (export or user-toggled-off).
      // The whole block is skipped — no DOM nodes created, no event listeners attached.
      // This means doors can't be re-positioned while labels are hidden, which is
      // actually desirable: if the user wants a clean printed view, they shouldn't
      // be accidentally dragging things.
      if (showDoorChrome) {
        const handleX = pxMid.x + outX * handleOff;
        const handleY = pxMid.y + outY * handleOff;
        const handle = mk('circle', {
          cx: handleX, cy: handleY, r: 6,
          fill: clr, opacity: 0.85,
          stroke: '#040b14', 'stroke-width': 1.2,
          style: 'cursor:grab;',
        });
        // Larger invisible hit area underneath for forgiveness on small misses.
        const handleHit = mk('circle', {
          cx: handleX, cy: handleY, r: 18,
          fill: clr, opacity: 0,
          style: 'cursor:grab;',
        });
        // Arrow icon — rotate to align with wall direction so it visually points along
        // the drag axis. Computed in degrees for the SVG transform attribute.
        const wallAngleDeg = Math.atan2(ends.unitVec.y, ends.unitVec.x) * 180 / Math.PI;
        const arrow = mkTxt(handleX, handleY + 2.2, '↔', '#040b14', 6.5);
        arrow.setAttribute('transform', `rotate(${wallAngleDeg}, ${handleX}, ${handleY + 1})`);
        arrow.setAttribute('pointer-events', 'none');
        const startDrag = e => {
          e.stopPropagation();
          doorDragRef.current = { mode: 'move', id: d.id, wallId: d.wallId };
        };
        handle.addEventListener('mousedown', startDrag);
        handleHit.addEventListener('mousedown', startDrag);
        svg.appendChild(handleHit); // hit area first so visible handle stays on top
        svg.appendChild(handle);
        svg.appendChild(arrow);
      }

      // ─── Position label (offset from wall start) ───
      // Hidden when:
      //   - shop.showWallMeasurements === false (Shane's measurements toggle), OR
      //   - hideChrome === true (export mode — door position is visually obvious
      //     from the door line itself, so the @offset text is just clutter)
      // Treated as ON by default so legacy projects render unchanged in the editor.
      if (!hideChrome && shop.showWallMeasurements !== false) {
        svg.appendChild(mkTxt(
          pxMid.x + outX * labelPosOff,
          pxMid.y + outY * labelPosOff,
          `@ ${d.offset.toFixed(1)}${shop.unit}`,
          clr, 7, 'middle'
        ));
      }

      // ─── Width-resize handles (overhead doors only) ───
      // Small visible squares (7px) sitting at each door endpoint, with
      // generous 18px invisible hit areas underneath. Smaller visible
      // footprint keeps the canvas readable on large shops with many doors.
      //
      // Hidden in export mode and when user toggles door labels off — same logic
      // as the drag handles above: these are editing chrome, not part of the plan.
      if (showDoorChrome && d.type === 'overhead') {
        const cursor = isMostlyHorizontal ? 'ew-resize' : 'ns-resize';
        [pxStart, pxEnd].forEach((endPoint, idx) => {
          const startWidthDrag = ev => {
            ev.stopPropagation();
            doorDragRef.current = {
              mode: 'width',
              id: d.id,
              wallId: d.wallId,
              startWidth: doorFt,
              startMx: ev.clientX,
              startMy: ev.clientY,
              endIndex: idx, // 0 = start side, 1 = end side
            };
          };
          // Larger invisible hit area
          const whHit = mk('rect', {
            x: endPoint.x - 9, y: endPoint.y - 9,
            width: 18, height: 18,
            fill: clr, opacity: 0,
            style: `cursor:${cursor};`,
          });
          // Visible handle on top — smaller than before so it doesn't dominate
          const wh = mk('rect', {
            x: endPoint.x - 3.5, y: endPoint.y - 3.5,
            width: 7, height: 7,
            fill: clr, opacity: 0.8,
            stroke: '#040b14', 'stroke-width': 0.8,
            rx: 1.5,
            style: `cursor:${cursor};`,
          });
          whHit.addEventListener('mousedown', startWidthDrag);
          wh.addEventListener('mousedown', startWidthDrag);
          svg.appendChild(whHit);
          svg.appendChild(wh);
        });
      }
    });

    // EXTERIOR DIMENSION LINES — rectangle mode only.
    // For rectangles we show the classic "60 ft × 40 ft" overall dimensions.
    // For free-form polygons we show per-wall length labels instead (below).
    //
    // Entire block is gated by shop.showWallMeasurements (toolbar/setup toggle).
    // Default ON (undefined treated as true) so legacy projects render unchanged.
    // Note: corner labels (NW/NE/SW/SE) stay visible regardless — they're orientation
    // aids, not measurements, and aren't part of what Shane wanted hidden.
    const showWallMeas = shop.showWallMeasurements !== false;
    if (!isFreeForm) {
      if (showWallMeas) {
        const dimY = oy - 36;
        svg.appendChild(mk('line', { x1: ox, y1: oy, x2: ox, y2: dimY, stroke: C.dim, 'stroke-width': 0.5, 'stroke-dasharray': '3,3', opacity: 0.5 }));
        svg.appendChild(mk('line', { x1: ox + shop.w * scale, y1: oy, x2: ox + shop.w * scale, y2: dimY, stroke: C.dim, 'stroke-width': 0.5, 'stroke-dasharray': '3,3', opacity: 0.5 }));
        const wl = mk('line', { x1: ox, y1: dimY, x2: ox + shop.w * scale, y2: dimY, stroke: C.dim, 'stroke-width': 0.9 });
        wl.setAttribute('marker-start', 'url(#aT)'); wl.setAttribute('marker-end', 'url(#aH)'); svg.appendChild(wl);
        svg.appendChild(mkTxt(ox + shop.w * scale / 2, dimY - 6, `${shop.w} ${shop.unit.toUpperCase()}`, C.dimTxt, 10));
        const dimX = ox + shop.w * scale + 36;
        svg.appendChild(mk('line', { x1: ox + shop.w * scale, y1: oy, x2: dimX, y2: oy, stroke: C.dim, 'stroke-width': 0.5, 'stroke-dasharray': '3,3', opacity: 0.5 }));
        svg.appendChild(mk('line', { x1: ox + shop.w * scale, y1: oy + shop.h * scale, x2: dimX, y2: oy + shop.h * scale, stroke: C.dim, 'stroke-width': 0.5, 'stroke-dasharray': '3,3', opacity: 0.5 }));
        const hl = mk('line', { x1: dimX, y1: oy, x2: dimX, y2: oy + shop.h * scale, stroke: C.dim, 'stroke-width': 0.9 });
        hl.setAttribute('marker-start', 'url(#aT)'); hl.setAttribute('marker-end', 'url(#aH)'); svg.appendChild(hl);
        svg.appendChild(mkTxt(dimX + 6, oy + shop.h * scale / 2 + 4, `${shop.h} ${shop.unit.toUpperCase()}`, C.dimTxt, 10, 'start'));
      }

      // NW/NE/SW/SE corner labels — rect-only since corners aren't meaningful on polygons.
      // These stay visible even when measurements are hidden — they're orientation, not dimensions.
      [[ox + 4, oy + 10, 'NW', 'start'], [ox + shop.w * scale - 4, oy + 10, 'NE', 'end'],
        [ox + 4, oy + shop.h * scale - 4, 'SW', 'start'], [ox + shop.w * scale - 4, oy + shop.h * scale - 4, 'SE', 'end'],
      ].forEach(([cx, cy, lbl, anchor]) => {
        const t = mkTxt(cx, cy, lbl, C.wall, 7, anchor); t.setAttribute('opacity', 0.3); svg.appendChild(t);
      });
    } else if (showWallMeas) {
      // FREE-FORM: per-wall length labels next to each wall midpoint.
      // The label sits perpendicular to the wall, offset slightly outward so it
      // doesn't sit on top of the wall line itself.
      // (Whole block skipped when measurements are toggled off.)
      const verts = shop.vertices || [];
      verts.forEach((a, i) => {
        const b = verts[(i + 1) % verts.length];
        const lenFt = Math.hypot(b.x - a.x, b.y - a.y);
        if (lenFt < 0.5) return; // skip degenerate walls
        const mxFt = (a.x + b.x) / 2;
        const myFt = (a.y + b.y) / 2;
        // Perpendicular offset (outward) — using the centroid as a rough "inside"
        // reference so we always offset away from the shop interior.
        let cxFt = 0, cyFt = 0;
        verts.forEach(v => { cxFt += v.x; cyFt += v.y; });
        cxFt /= verts.length; cyFt /= verts.length;
        const dx = b.x - a.x, dy = b.y - a.y;
        const perpX = -dy / lenFt;
        const perpY = dx / lenFt;
        // Flip perpendicular if it points toward the centroid (we want outward)
        const dotToCentroid = (cxFt - mxFt) * perpX + (cyFt - myFt) * perpY;
        const sign = dotToCentroid > 0 ? -1 : 1;
        const offsetFt = 1.8; // ~visual offset in shop units; converted to px below
        const labelXFt = mxFt + perpX * sign * offsetFt;
        const labelYFt = myFt + perpY * sign * offsetFt;
        const labelPx = { x: ox + labelXFt * scale, y: oy + labelYFt * scale };
        const t = mkTxt(labelPx.x, labelPx.y + 3, `${lenFt.toFixed(1)} ${shop.unit}`, C.dimTxt, 9);
        t.setAttribute('font-weight', '600');
        svg.appendChild(t);
      });
    }

    // CARDINAL COMPASS LABELS — rectangle mode only.
    // For free-form shapes the per-wall length labels already orient the user,
    // and the cardinal labels were either redundant or in the way.
    if (!isFreeForm) {
      [
        { label: 'N', x: ox + shop.w * scale / 2, y: oy - 16,                  anchor: 'middle', color: C.compassN },
        { label: 'S', x: ox + shop.w * scale / 2, y: oy + shop.h * scale + 24, anchor: 'middle', color: C.wall },
        { label: 'W', x: ox - 18,                 y: oy + shop.h * scale / 2 + 4, anchor: 'middle', color: C.wall },
        { label: 'E', x: ox + shop.w * scale + 18, y: oy + shop.h * scale / 2 + 4, anchor: 'middle', color: C.wall },
      ].forEach(({ label, x, y, anchor, color }) => {
        const t = mkTxt(x, y, label, color, 12, anchor);
        t.setAttribute('font-weight', '700'); t.setAttribute('opacity', '0.9');
        svg.appendChild(t);
      });
    }

    // USER DIMENSION LINES
    dimensions.forEach(dim => {
      const isSel = dim.id === selectedDim;
      const sx1 = ox + dim.x1 * scale;
      const sy1 = oy + dim.y1 * scale;
      const sx2 = ox + dim.x2 * scale;
      const sy2 = oy + dim.y2 * scale;
      const dist = calcDist(dim.x1, dim.y1, dim.x2, dim.y2);
      const label = `${dist.toFixed(1)} ${shop.unit}`;
      const color = isSel ? '#ffffff' : C.measure;
      const result = drawDimLine(svg, sx1, sy1, sx2, sy2, label, color, isSel, dim.offset ?? -30);
      if (result) {
        // Shared mousedown handler — fires on either the dim bar hit strip or the
        // larger invisible hit circle around the center handle. Stores the
        // perpendicular axis (nx, ny) so drag math projects mouse movement onto it
        // correctly regardless of line orientation.
        const startDrag = e => {
          e.stopPropagation();
          didDragRef.current = false;
          setSelectedDim(dim.id);
          setSelected([]); setSelectedDrain(null);
          interactRef.current = {
            mode: 'dimOffset', id: dim.id,
            startX: e.clientX, startY: e.clientY,
            startOffset: dim.offset ?? -30,
            nx: result.nx, ny: result.ny,
          };
        };
        result.hit.addEventListener('mousedown', startDrag);
        if (result.centerHit) result.centerHit.addEventListener('mousedown', startDrag);
        svg.appendChild(result.g);
      }
    });

    // PREVIEW LINE while drawing
    if (dimMode && dimStart) {
      const sx1 = ox + dimStart.x * scale;
      const sy1 = oy + dimStart.y * scale;
      const shopMouse = toShop(mouse.x, mouse.y, ox, oy, scale);
      // Apply ortho snap to the rubber-band end point unless Alt is held.
      // This makes the preview show the same line that will be committed on click,
      // so users see exactly what they're drawing.
      const endShop = mouse.alt
        ? { x: shopMouse.x, y: shopMouse.y }
        : orthoSnap(dimStart.x, dimStart.y, shopMouse.x, shopMouse.y);
      const endX = ox + endShop.x * scale;
      const endY = oy + endShop.y * scale;
      const dist = calcDist(dimStart.x, dimStart.y, endShop.x, endShop.y);
      const label = `${dist.toFixed(1)} ${shop.unit}`;
      svg.appendChild(mk('circle', { cx: sx1, cy: sy1, r: 5, fill: C.measure, opacity: 0.9 }));
      svg.appendChild(mk('line', { x1: sx1, y1: sy1, x2: endX, y2: endY, stroke: C.measure, 'stroke-width': 1, 'stroke-dasharray': '6,3', opacity: 0.7 }));
      const mx = (sx1 + endX) / 2;
      const my = (sy1 + endY) / 2;
      const bgW = label.length * 5.5 + 10;
      svg.appendChild(mk('rect', { x: mx - bgW / 2, y: my - 8, width: bgW, height: 14, fill: '#03080f', rx: 2, opacity: 0.85 }));
      svg.appendChild(mkTxt(mx, my + 4, label, C.measure, 8.5));
    }

    // ─── ROOM DRAW PREVIEW ───────────────────────────────────────────
    // Render the in-progress polygon while the user is placing corners.
    // - Yellow placed lines between consecutive vertices
    // - Yellow dot at each placed vertex
    // - Green dot at start vertex once ≥3 vertices (close target)
    // - Preview line from last vertex to current cursor (snapped)
    if (roomDrawMode && roomDrawVerts.length > 0) {
      const roomClr = '#ffe500';     // matches default room border color
      const closeClr = '#00cc88';    // green = "click here to close" target
      // Placed wall lines
      for (let i = 0; i < roomDrawVerts.length - 1; i++) {
        const a = roomDrawVerts[i], b = roomDrawVerts[i + 1];
        svg.appendChild(mk('line', {
          x1: ox + a.x * scale, y1: oy + a.y * scale,
          x2: ox + b.x * scale, y2: oy + b.y * scale,
          stroke: roomClr, 'stroke-width': 1.5, opacity: 0.85,
        }));
      }
      // Vertex dots (drawn after lines so they sit on top)
      roomDrawVerts.forEach((v, idx) => {
        const isStart = idx === 0;
        const showAsCloseTarget = isStart && roomDrawVerts.length >= 3;
        svg.appendChild(mk('circle', {
          cx: ox + v.x * scale, cy: oy + v.y * scale,
          r: showAsCloseTarget ? 6 : 4,
          fill: showAsCloseTarget ? closeClr : roomClr,
          stroke: '#040b14', 'stroke-width': 1, opacity: 0.95,
        }));
      });
      // Preview rubber-band line from last placed vertex to cursor
      const last = roomDrawVerts[roomDrawVerts.length - 1];
      const shopMouse = toShop(mouse.x, mouse.y, ox, oy, scale);
      const px = doSnap(shopMouse.x);
      const py = doSnap(shopMouse.y);
      // If cursor is over the start vertex (close target), don't snap the preview line
      const overStart = isOverStartVertex(shopMouse.x, shopMouse.y, roomDrawVerts);
      const endX = ox + (overStart ? roomDrawVerts[0].x : px) * scale;
      const endY = oy + (overStart ? roomDrawVerts[0].y : py) * scale;
      svg.appendChild(mk('line', {
        x1: ox + last.x * scale, y1: oy + last.y * scale,
        x2: endX, y2: endY,
        stroke: roomClr, 'stroke-width': 1.2,
        'stroke-dasharray': '5,4', opacity: 0.6,
      }));
      // Live length label on preview line
      const lastX = ox + last.x * scale, lastY = oy + last.y * scale;
      const previewLen = calcDist(last.x, last.y, overStart ? roomDrawVerts[0].x : px, overStart ? roomDrawVerts[0].y : py);
      if (previewLen > 0.5) {
        const lblX = (lastX + endX) / 2;
        const lblY = (lastY + endY) / 2 - 6;
        const lbl = `${previewLen.toFixed(1)} ${shop.unit}`;
        const bgW = lbl.length * 5.5 + 10;
        svg.appendChild(mk('rect', { x: lblX - bgW / 2, y: lblY - 7, width: bgW, height: 12, fill: '#03080f', rx: 2, opacity: 0.85 }));
        svg.appendChild(mkTxt(lblX, lblY + 3, lbl, roomClr, 8));
      }
    }

    // DRAG SELECT BOX
    if (dragBox) {
      const bx = Math.min(dragBox.x1, dragBox.x2);
      const by = Math.min(dragBox.y1, dragBox.y2);
      const bw = Math.abs(dragBox.x2 - dragBox.x1);
      const bh = Math.abs(dragBox.y2 - dragBox.y1);
      svg.appendChild(mk('rect', { x: bx, y: by, width: bw, height: bh, fill: 'rgba(51,153,255,0.08)', stroke: C.accent, 'stroke-width': 1, 'stroke-dasharray': '4,3' }));
    }

    // MOUSE COORD — only show when cursor is inside the shop.
    // For free-form polygons we use point-in-polygon; rectangles use the simpler bbox check.
    const sc2 = toShop(mouse.x, mouse.y, ox, oy, scale);
    const insideShop = (shop.shapeType === 'free' && shop.vertices && shop.vertices.length >= 3)
      ? pointInPolygon(sc2, shop.vertices)
      : (sc2.x >= 0 && sc2.x <= shop.w && sc2.y >= 0 && sc2.y <= shop.h);
    if (insideShop) {
      const t = mkTxt(mouse.x + 10, mouse.y - 7, `${snap(sc2.x, 0.01).toFixed(2)}, ${snap(sc2.y, 0.01).toFixed(2)} ${shop.unit}`, C.accent, 8, 'start');
      t.setAttribute('opacity', 0.75); svg.appendChild(t);
    }
  }, [size, mouse, shop, doors, placed, drains, dimensions, selectedDim, selected, selectedDrain, scale, ox, oy, zoom, dragBox, dimMode, dimStart, showGrid, showBayZones, hideChrome, svgRef, roomDrawMode, roomDrawVerts]);

  useEffect(() => { drawAll(); }, [drawAll]);

  const onMouseMove = e => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    setMouse({ x: mx, y: my, alt: e.altKey });

    if (panRef.current) {
      const dx = mx - panRef.current.startX;
      const dy = my - panRef.current.startY;
      const newPan = { x: panRef.current.startPanX + dx, y: panRef.current.startPanY + dy };
      panStateRef.current = newPan; setPan(newPan); return;
    }
    if (dragBoxRef.current) {
      setDragBox({ x1: dragBoxRef.current.startX, y1: dragBoxRef.current.startY, x2: mx, y2: my });
      didDragRef.current = true; return;
    }
    if (drainDragRef.current) {
      const dd = drainDragRef.current;
      if (dd.mode === 'move') {
        didDragRef.current = true;
        const sc = toShop(mx, my, ox, oy, scale);
        setDrains(prev => prev.map(d => d.id === dd.id ? { ...d, cx: doSnap(sc.x - dd.offX), cy: doSnap(sc.y - dd.offY) } : d));
      }
      if (dd.mode === 'scale') {
        const sc = toShop(mx, my, ox, oy, scale);
        const drain = drains.find(d => d.id === dd.id);
        if (drain) {
          setDrains(prev => prev.map(d => d.id === dd.id
            ? { ...d, w: Math.max(0.5, doSnap(Math.abs(sc.x - drain.cx) * 2)), h: Math.max(0.5, doSnap(Math.abs(sc.y - drain.cy) * 2)) }
            : d));
        }
      }
      return;
    }
    if (doorDragRef.current) {
      const dd = doorDragRef.current;
      // Get the wall this door is attached to
      const wallEnds = getWallEndpoints(shop, dd.wallId);
      if (!wallEnds || wallEnds.length === 0) {
        // Wall vanished mid-drag — abort and clear the ref to prevent stuck state
        doorDragRef.current = null;
        return;
      }

      if (dd.mode === 'move') {
        // Project the cursor onto the wall to get an offset along it.
        // We anchor on the door's CENTER under the cursor so the door follows
        // the mouse exactly rather than jumping by half its width.
        const sc = toShop(mx, my, ox, oy, scale);
        const proj = projectPointOntoWall(sc, wallEnds.a, wallEnds.b);
        // Find the door's current width to compute the start offset
        const door = doors.find(d => d.id === dd.id);
        const doorW = door ? (door.width || (door.type === 'overhead' ? 10 : 4)) : 4;
        // Cursor maps to door center → start offset = cursor offset - half width
        const newOffset = Math.max(0, Math.min(wallEnds.length - doorW, proj.offset - doorW / 2));
        setDoors(prev => prev.map(d => d.id === dd.id ? { ...d, offset: newOffset } : d));
      }

      if (dd.mode === 'width') {
        // Width drag: project cursor onto wall and recompute width based on
        // which end (start or end) of the door is being dragged.
        // For start-end drag (endIndex 0): the door's start vertex stays fixed,
        // the end follows the cursor, so width = max(0, cursorOffset - door.offset).
        // For end-end drag (endIndex 1): the door's end vertex follows cursor,
        // start stays at door.offset, so width = cursorOffset - door.offset.
        const sc = toShop(mx, my, ox, oy, scale);
        const proj = projectPointOntoWall(sc, wallEnds.a, wallEnds.b);
        const door = doors.find(d => d.id === dd.id);
        if (!door) return;
        let newWidth;
        if (dd.endIndex === 0) {
          // Dragging the start handle — door's far end is at door.offset + door.width.
          // Move start to cursor offset; width adjusts so end stays put.
          const farEnd = door.offset + door.width;
          const newOffset = Math.max(0, Math.min(farEnd - 4, proj.offset));
          newWidth = farEnd - newOffset;
          newWidth = Math.max(4, Math.min(30, Math.round(newWidth * 2) / 2));
          // Recompute newOffset so the rounded width keeps the far end in place
          const adjOffset = farEnd - newWidth;
          setDoors(prev => prev.map(d => d.id === dd.id
            ? { ...d, offset: Math.max(0, adjOffset), width: newWidth }
            : d));
        } else {
          // Dragging the end handle — door's start stays at door.offset.
          // New width = cursor offset - door.offset, clamped.
          newWidth = proj.offset - door.offset;
          newWidth = Math.max(4, Math.min(30, Math.round(newWidth * 2) / 2));
          // Also clamp so door doesn't exceed wall length
          const maxWidth = wallEnds.length - door.offset;
          newWidth = Math.min(newWidth, maxWidth);
          setDoors(prev => prev.map(d => d.id === dd.id ? { ...d, width: newWidth } : d));
        }
      }
      return;
    }
    const ia = interactRef.current;
    if (!ia) return;
    const sc = toShop(mx, my, ox, oy, scale);

    if (ia.mode === 'dimOffset') {
      didDragRef.current = true;
      // Project the raw mouse delta onto the perpendicular axis of the dim line.
      // This is what makes vertical measurements drag side-to-side and horizontal
      // measurements drag up-down — the offset always moves perpendicular to the
      // measurement, never just along Y.
      const dxPx = e.clientX - ia.startX;
      const dyPx = e.clientY - ia.startY;
      const projected = dxPx * ia.nx + dyPx * ia.ny;
      const newOffset = ia.startOffset + projected;
      setDimensions(prev => prev.map(d =>
        d.id === ia.id ? { ...d, offset: newOffset } : d
      ));
      return;
    }
    if (ia.mode === 'move') {
      didDragRef.current = true;
      setPlaced(prev => prev.map(p => {
        if (p.uid !== ia.uid) return p;
        const newCx = doSnap(sc.x - ia.offX);
        const newCy = doSnap(sc.y - ia.offY);
        // Polygon rooms: translate all vertices by the delta from old center.
        // For rect items: just update cx/cy as before.
        if (p.shapeType === 'free' && p.vertices) {
          const dx = newCx - p.cx;
          const dy = newCy - p.cy;
          return {
            ...p,
            cx: newCx, cy: newCy,
            vertices: p.vertices.map(v => ({ x: v.x + dx, y: v.y + dy })),
            _dragging: true,
          };
        }
        return { ...p, cx: newCx, cy: newCy, _dragging: true };
      }));
    }
    if (ia.mode === 'moveMulti') {
      didDragRef.current = true;
      const dx = doSnap(sc.x - ia.startMx);
      const dy = doSnap(sc.y - ia.startMy);
      setPlaced(prev => prev.map(p => {
        const start = ia.starts.find(s => s.uid === p.uid);
        if (!start) return p;
        // Multi-move: polygon rooms ALSO need their vertices translated by
        // the same dx, dy. The original vertex positions are NOT captured in
        // starts (only cx/cy), so we reconstruct vertex deltas from current.
        if (p.shapeType === 'free' && p.vertices && start.vertices) {
          return {
            ...p,
            cx: start.cx + dx, cy: start.cy + dy,
            vertices: start.vertices.map(v => ({ x: v.x + dx, y: v.y + dy })),
            _dragging: true,
          };
        }
        return { ...p, cx: start.cx + dx, cy: start.cy + dy, _dragging: true };
      }));
    }
    if (ia.mode === 'rotate') {
      didDragRef.current = true;
      const item = placed.find(p => p.uid === ia.uid); if (!item) return;
      const cpx = ox + item.cx * scale, cpy = oy + item.cy * scale;
      const angle = Math.atan2(my - cpy, mx - cpx) * 180 / Math.PI + 90;
      const rot = e.shiftKey ? ((angle % 360) + 360) % 360 : Math.round(((angle % 360) + 360) % 360 / 15) * 15;
      setPlaced(prev => prev.map(p => p.uid === ia.uid ? { ...p, rot } : p));
    }
    if (ia.mode.startsWith('scale')) {
      didDragRef.current = true;
      const item = placed.find(p => p.uid === ia.uid); if (!item) return;
      const cpx = ox + item.cx * scale, cpy = oy + item.cy * scale;
      const dx = mx - cpx, dy = my - cpy;
      const r = deg2rad(-item.rot);
      const lx = dx * Math.cos(r) - dy * Math.sin(r);
      const ly = dx * Math.sin(r) + dy * Math.cos(r);
      const newSX = Math.min(Math.max(2 / item.fw, Math.abs(lx) / scale / (item.fw / 2)), shop.w / item.fw);
      const newSY = Math.min(Math.max(2 / item.fh, Math.abs(ly) / scale / (item.fh / 2)), shop.h / item.fh);
      setPlaced(prev => prev.map(p => p.uid === ia.uid ? { ...p, scaleX: newSX, scaleY: newSY } : p));
    }
  };

  const onMouseDown = e => {
    // Alt+drag normally pans the canvas, but during measurement drawing we
    // repurpose Alt to mean "bypass ortho snap" — so don't start a pan while
    // the user is mid-measurement. Middle-click pan still works as a fallback.
    if (e.button === 1 || (e.button === 0 && e.altKey && !dimModeRef.current)) {
      e.preventDefault();
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      panRef.current = { startX: e.clientX - rect.left, startY: e.clientY - rect.top, startPanX: panStateRef.current.x, startPanY: panStateRef.current.y };
    }
  };

  const onMouseUp = () => {
    if (panRef.current) { panRef.current = null; return; }
    if (dragBoxRef.current && dragBox) {
      const bx1 = Math.min(dragBox.x1, dragBox.x2);
      const by1 = Math.min(dragBox.y1, dragBox.y2);
      const bx2 = Math.max(dragBox.x1, dragBox.x2);
      const by2 = Math.max(dragBox.y1, dragBox.y2);
      const inBox = placed.filter(item => {
        const sx = ox + item.cx * scale;
        const sy = oy + item.cy * scale;
        return sx >= bx1 && sx <= bx2 && sy >= by1 && sy <= by2;
      });
      if (inBox.length > 0) { setSelected(inBox.map(i => i.uid)); setSelectedDrain(null); setSelectedDim(null); }
      dragBoxRef.current = null; setDragBox(null); return;
    }
    // ─── Commit history on gesture end ────────────────────────────
    // Only commit if didDragRef indicates the user actually changed
    // something (mouse moved during the press). Pure clicks don't push
    // history entries, which keeps the undo stack focused on real edits.
    const wasDrag = didDragRef.current;
    const draggingDrain = !!drainDragRef.current;
    const draggingDoor = !!doorDragRef.current;
    drainDragRef.current = null;
    doorDragRef.current = null;
    const ia = interactRef.current;
    if (ia?.mode === 'move' || ia?.mode === 'moveMulti') {
      setPlaced(prev => prev.map(p => selected.includes(p.uid) ? { ...p, _dragging: false } : p));
    }
    interactRef.current = null;
    // Push a snapshot if any meaningful drag completed. Covers item move/
    // rotate/scale, dim offset drag, drain move/scale, door drag.
    if (wasDrag && (ia || draggingDrain || draggingDoor)) {
      commitHistory && commitHistory();
    }
  };

  const onSvgClick = e => {
    // ─── Free-form room drawing ──────────────────────────────────────
    // While in drawing mode, every left-click places a polygon vertex
    // (snapped to the current grid). If the click is within range of the
    // start vertex AND we have ≥3 vertices, close the polygon and create
    // the room. Cancellation is handled by ESC / sidebar button toggle.
    if (roomDrawModeRef.current) {
      if (e.button !== 0) return;
      const rect = svgRef.current.getBoundingClientRect();
      const sc = toShop(e.clientX - rect.left, e.clientY - rect.top, ox, oy, scale);
      const x = doSnap(sc.x);
      const y = doSnap(sc.y);
      const current = roomDrawVertsRef.current;

      // Check if user is closing the polygon by clicking near start vertex
      if (isOverStartVertex(sc.x, sc.y, current)) {
        // Validate: at least 3 vertices, no self-intersection
        if (current.length < 3) {
          showToast && showToast('Need at least 3 corners to close the room.', 'error');
          return;
        }
        if (isSelfIntersecting(current)) {
          showToast && showToast('Room outline crosses itself — fix before closing.', 'error');
          return;
        }
        const newRoom = makePolygonRoom(current, 'Room');
        setPlaced(prev => [newRoom, ...prev]);
        setSelected([newRoom.uid]);
        setSelectedDrain(null);
        setSelectedDim(null);
        if (setRoomDrawMode) setRoomDrawMode(false);
        commitHistory && commitHistory();
        return;
      }

      // Otherwise, append the vertex
      const next = [...current, { x, y }];
      roomDrawVertsRef.current = next;
      setRoomDrawVerts(next);
      return;
    }

    if (dimModeRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      const sc = toShop(e.clientX - rect.left, e.clientY - rect.top, ox, oy, scale);
      if (!dimStart) {
        setDimStart({ x: doSnap(sc.x), y: doSnap(sc.y) });
      } else {
        // Apply ortho snap to the second point (matches what the preview shows).
        // Holding Alt bypasses ortho for free-angle measurements.
        const rawX = doSnap(sc.x);
        const rawY = doSnap(sc.y);
        const end = e.altKey
          ? { x: rawX, y: rawY }
          : orthoSnap(dimStart.x, dimStart.y, rawX, rawY);
        const newDim = { id: `dim-${Date.now()}`, x1: dimStart.x, y1: dimStart.y, x2: end.x, y2: end.y, offset: -30 };
        setDimensions(prev => [...prev, newDim]);
        setSelectedDim(newDim.id);
        setDimStart(null);
        commitHistory && commitHistory();
      }
      return;
    }
    if (!didDragRef.current && e.target === svgRef.current) {
      setSelected([]); setSelectedDrain(null); setSelectedDim(null);
    }
    didDragRef.current = false;
  };

  const onSvgMouseDown = e => {
    onMouseDown(e);
    // Drag-box selection is suppressed while in measure or room-drawing modes.
    if (!dimModeRef.current && !roomDrawModeRef.current && e.button === 0 && !e.altKey && e.target === svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      dragBoxRef.current = { startX: e.clientX - rect.left, startY: e.clientY - rect.top };
      didDragRef.current = false;
    }
  };

  const onDragOver = e => e.preventDefault();

  const onDrop = e => {
    e.preventDefault();
    const eqId = e.dataTransfer.getData('eqId');
    const { CATALOG } = require('../data/catalog');
    const eq = CATALOG.find(x => x.id === eqId);
    if (!eq) return;
    const rect = svgRef.current.getBoundingClientRect();
    const sc = toShop(e.clientX - rect.left, e.clientY - rect.top, ox, oy, scale);
    const newItem = {
      ...JSON.parse(JSON.stringify(eq)),
      uid: `${Date.now()}-${Math.random()}`,
      cx: doSnap(sc.x), cy: doSnap(sc.y),
      rot: 0, scaleX: 1, scaleY: 1, label: '',
    };
    setPlaced(prev => [...prev, newItem]);
    setSelected([newItem.uid]);
    setSelectedDrain(null); setSelectedDim(null);
    commitHistory && commitHistory();
  };

  const isAtDefault = zoom === 1 && pan.x === 0 && pan.y === 0;

  // ─── Per-lift bay zone override button state ──────────────────────
  // Computed BEFORE the return so the JSX stays simple (no IIFE).
  // Resolves to null when the button should not be shown.
  // Legacy fallback: look up bayFootprint from the catalog if the item itself
  // is missing it (older saved projects predate the field on the item).
  let bayBtn = null;
  if (selected.length === 1) {
    const selItem = placed.find(p => p.uid === selected[0]);
    if (selItem) {
      let footprint = selItem.bayFootprint;
      if (!footprint) {
        const cat = require('../data/catalog').CATALOG.find(c => c.id === selItem.id);
        footprint = cat && cat.bayFootprint;
      }
      if (footprint) {
        const perLift = selItem.showBayZone;
        const effective = perLift === undefined ? showBayZones : perLift;
        // Cycle: Auto (undefined) -> On (true) -> Off (false) -> Auto ...
        const nextState = perLift === undefined ? true : perLift === true ? false : undefined;
        const stateLabel = perLift === undefined ? 'Auto' : perLift ? 'On' : 'Off';
        bayBtn = {
          effective,
          stateLabel,
          onClick: () => {
            setPlaced(prev => prev.map(p => p.uid === selItem.uid ? { ...p, showBayZone: nextState } : p));
            if (commitHistory) commitHistory();
          },
        };
      }
    }
  }

  // ─── Selected lift, for the "simulate approach" toggle ─────────────
  // Same selection pattern as bayBtn above. Only offered for catalog ids
  // the solver knows how to build a target pose for (see
  // vehiclePath.js's SIMULATABLE_LIFT_IDS).
  let simLiftItem = null;
  if (selected.length === 1) {
    const selItem = placed.find(p => p.uid === selected[0]);
    if (selItem && SIMULATABLE_LIFT_IDS.includes(selItem.id)) {
      simLiftItem = selItem;
    }
  }
  return (
    // height: 100% matters — the parent wrapper in App.js is display:block, so
    // flex:1 alone did nothing and this div sized itself to its own SVG. That
    // locked the canvas at its 600px starting height forever (ResizeObserver
    // just measured its own content back). Filling the parent breaks the loop.
    <div ref={areaRef} style={{ flex: 1, height: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg, position: 'relative' }}>

      {/* Per-lift bay zone override button. Cycles Auto -> On -> Off -> Auto. */}
      {bayBtn && (
        <button onClick={bayBtn.onClick} title={'Bay zone for this lift: ' + bayBtn.stateLabel + ' (click to cycle)'} style={{
          position: 'absolute', top: 12, left: 12, zIndex: 10,
          background: bayBtn.effective ? 'rgba(255,170,40,.18)' : 'rgba(2,12,28,0.85)',
          border: '1px solid ' + (bayBtn.effective ? '#ffaa2888' : '#3399ff88'),
          borderRadius: 7, color: bayBtn.effective ? '#ffaa28' : '#3399ff',
          fontSize: 11, fontWeight: 700, padding: '7px 14px',
          cursor: 'pointer', fontFamily: 'inherit',
          boxShadow: '0 2px 12px rgba(0,0,0,.5)',
        }}>
          Bay Zone: {bayBtn.stateLabel}
        </button>
      )}

      {/* Per-lift "simulate approach" toggle — same left-edge stack as the
          Bay Zone button, offset down when both are showing for the same lift. */}
      {simLiftItem && (
        <button onClick={() => setSimActive(v => !v)} title="Simulate a vehicle driving onto this lift" style={{
          position: 'absolute', top: bayBtn ? 50 : 12, left: 12, zIndex: 10,
          background: simActive ? 'rgba(51,153,255,.18)' : 'rgba(2,12,28,0.85)',
          border: '1px solid ' + (simActive ? '#3399ffcc' : '#3399ff88'),
          borderRadius: 7, color: '#3399ff',
          fontSize: 11, fontWeight: 700, padding: '7px 14px',
          cursor: 'pointer', fontFamily: 'inherit',
          boxShadow: '0 2px 12px rgba(0,0,0,.5)',
        }}>
          {simActive ? 'Close Simulation' : 'Simulate Approach'}
        </button>
      )}

      {/* Floating fit button */}
      {!isAtDefault && (
        <button onClick={resetZoom} title="Fit shop to screen (press 0)" style={{
          position: 'absolute', top: 12, right: 12, zIndex: 10,
          background: 'rgba(2,12,28,0.85)', border: '1px solid #3399ff88',
          borderRadius: 7, color: '#3399ff', fontSize: 11, fontWeight: 700,
          padding: '7px 14px', cursor: 'pointer', fontFamily: 'inherit',
          boxShadow: '0 2px 12px rgba(0,0,0,.5)',
        }}>
          ⊡ Fit to Screen
        </button>
      )}

      {/* Dim mode banner */}
      {dimMode && (
        <div style={{
          position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
          zIndex: 10, background: 'rgba(0,255,136,0.12)', border: '1px solid #00ff8866',
          borderRadius: 7, color: '#00ff88', fontSize: 11, fontWeight: 700,
          padding: '7px 18px', pointerEvents: 'none',
        }}>
          {dimStart ? '📏 Click second point to finish — hold Alt for free angle' : '📏 Click first point to start measurement'}
        </div>
      )}

      {/* Room draw mode banner — mirrors dimMode style but uses room-yellow
          accents. Shows different copy depending on how many vertices the user
          has placed: instructional → ready-to-close. */}
      {roomDrawMode && (
        <div style={{
          position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
          zIndex: 10, background: 'rgba(255,229,0,0.12)', border: '1px solid rgba(255,229,0,.5)',
          borderRadius: 7, color: '#ffe500', fontSize: 11, fontWeight: 700,
          padding: '7px 18px', pointerEvents: 'none',
        }}>
          {roomDrawVerts.length === 0
            ? '⬡ Click on the canvas to place the first corner — ESC to cancel'
            : roomDrawVerts.length < 3
              ? `⬡ ${roomDrawVerts.length} corner${roomDrawVerts.length === 1 ? '' : 's'} placed — keep clicking, Backspace to undo`
              : '⬡ Click the green dot to close the room, or keep adding corners — Backspace to undo'}
        </div>
      )}

      <svg ref={svgRef}
        style={{ display: 'block', cursor: 'crosshair', userSelect: 'none' }}
        onMouseMove={onMouseMove} onMouseDown={onSvgMouseDown}
        onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
        onDragOver={onDragOver} onDrop={onDrop}
        onClick={onSvgClick}
      />

      {/* Vehicle approach simulation — a sibling overlay, not part of the
          imperative drawAll() SVG above (see VehiclePathOverlay.jsx header
          for why). Shares scale/ox/oy so it lines up with the drawn shop. */}
      {simActive && simLiftItem && (
        <VehiclePathOverlay
          shop={shop}
          doors={doors}
          placed={placed}
          liftItem={simLiftItem}
          vehicleKey={simVehicleKey}
          onVehicleKeyChange={setSimVehicleKey}
          doorId={simDoorId}
          onDoorIdChange={setSimDoorId}
          pathMode={simPathMode}
          onPathModeChange={setSimPathMode}
          waypoints={simManualWaypoints}
          onWaypointsChange={setSimManualWaypoints}
          goalGear={simManualGoalGear}
          onGoalGearChange={setSimManualGoalGear}
          goalFlip={simManualGoalFlip}
          onGoalFlipChange={setSimManualGoalFlip}
          speed={simSpeed}
          onSpeedChange={setSimSpeed}
          scale={scale}
          ox={ox}
          oy={oy}
          size={{ width: size.w, height: size.h }}
        />
      )}

      {/* Status bar */}
      <div style={{ padding: '0 12px', height: 42, borderTop: '1px solid #162840', display: 'flex', gap: 10, fontSize: 11, fontWeight: 500, color: '#7f9fbd', flexShrink: 0, background: '#07111e', alignItems: 'center', overflow: 'visible', position: 'relative' }}>

        {/* ===== SNAP DROPDOWN =====================================
            Was 5 inline pill buttons; now collapsed to one trigger
            that shows the current value and drops up a menu. */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={() => { setShowSnapMenu(m => !m); setShowLabelsMenu(false); setShowShortcutsMenu(false); }}
            title="Snap-to-grid spacing"
            style={{
              background: 'transparent', border: '1px solid #1e3a54',
              borderRadius: 4, color: '#8aa8c5',
              fontSize: 11, fontWeight: 600, padding: '4px 10px',
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
            <span style={{ color: '#7f9fbd', fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase' }}>Snap</span>
            <span style={{ color: '#c8dff0' }}>
              {(SNAP_OPTIONS.find(o => o.value === snapSize) || SNAP_OPTIONS[0]).label}
            </span>
            <span style={{ fontSize: 11, opacity: 0.6 }}>▾</span>
          </button>

          {showSnapMenu && (
            <>
              <div onClick={() => setShowSnapMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 998 }} />
              {/* Drops UP from the bottom bar — bottom anchored, opens above the button */}
              <div style={{
                position: 'absolute', bottom: 34, left: 0,
                background: '#0a1628', border: '1px solid #1e3a54',
                borderRadius: 8, padding: 4, minWidth: 110,
                boxShadow: '0 -4px 20px rgba(0,0,0,.6)', zIndex: 999,
              }}>
                {SNAP_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setSnapSize(opt.value); setShowSnapMenu(false); }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      background: snapSize === opt.value ? 'rgba(51,153,255,.15)' : 'transparent',
                      border: 'none', borderRadius: 4,
                      color: snapSize === opt.value ? '#3399ff' : '#c8dff0',
                      fontSize: 11, fontWeight: 600, padding: '6px 10px',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                    onMouseEnter={e => { if (snapSize !== opt.value) e.currentTarget.style.background = 'rgba(51,153,255,.06)'; }}
                    onMouseLeave={e => { if (snapSize !== opt.value) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div style={{ width: 1, height: 16, background: '#162840', flexShrink: 0 }} />

        {/* ===== LABELS DROP-UP ====================================
            Groups the four visibility toggles (Grid, Bay Zones, Wall
            Measurements, Door Labels). Each row is itself a toggle —
            clicking flips the underlying state without closing the menu,
            so users can toggle several without reopening. */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          {/* Trigger lights up if ANY of the four toggles is on (matches the eye-icon affordance) */}
          {(() => {
            const wallMeasOn = shop.showWallMeasurements !== false;
            const doorLblOn  = shop.showDoorLabels !== false;
            const anyOn = showGrid || showBayZones || wallMeasOn || doorLblOn;
            return (
              <button
                onClick={() => { setShowLabelsMenu(m => !m); setShowSnapMenu(false); setShowShortcutsMenu(false); }}
                title="Show/hide canvas labels & overlays"
                style={{
                  background: anyOn ? 'rgba(0,100,200,.15)' : 'transparent',
                  border: `1px solid ${anyOn ? '#3399ff66' : '#1e3a54'}`,
                  borderRadius: 4, color: anyOn ? '#3399ff' : '#8aa8c5',
                  fontSize: 11, fontWeight: 600, padding: '4px 10px',
                  cursor: 'pointer', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                {/* Eye icon — simple SVG inline so it inherits color */}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M2 12 C5 6 8 4 12 4 C16 4 19 6 22 12 C19 18 16 20 12 20 C8 20 5 18 2 12 Z"
                    stroke="currentColor" strokeWidth="2" fill="none" />
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
                Labels
                <span style={{ fontSize: 11, opacity: 0.6 }}>▴</span>
              </button>
            );
          })()}

          {showLabelsMenu && (
            <>
              <div onClick={() => setShowLabelsMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 998 }} />
              <div style={{
                position: 'absolute', bottom: 34, left: 0,
                background: '#0a1628', border: '1px solid #1e3a54',
                borderRadius: 8, padding: 6, minWidth: 220,
                boxShadow: '0 -4px 20px rgba(0,0,0,.6)', zIndex: 999,
              }}>
                <div style={{ fontSize: 11, color: '#7f9fbd', letterSpacing: '.07em', textTransform: 'uppercase', padding: '4px 10px 6px', fontWeight: 700 }}>
                  Show on canvas
                </div>

                {/* Each row: icon + label + check (or empty) + keyboard hint.
                    Clicking flips the state but does NOT close the menu so users
                    can toggle several in one go. */}

                {/* Grid */}
                <LabelRow
                  active={showGrid}
                  onClick={() => setShowGrid(g => !g)}
                  iconColor="#3399ff"
                  iconPath={<><rect x="3" y="3" width="18" height="18" rx="1" stroke="currentColor" strokeWidth="1.6" fill="none" /><path d="M3 9 H21 M3 15 H21 M9 3 V21 M15 3 V21" stroke="currentColor" strokeWidth="1.2" /></>}
                  label="Grid"
                  shortcut="G"
                />

                {/* Bay Zones — amber */}
                <LabelRow
                  active={showBayZones}
                  onClick={() => setShowBayZones(b => !b)}
                  iconColor="#ffaa28"
                  iconPath={<><rect x="3" y="3" width="18" height="18" rx="1" stroke="currentColor" strokeWidth="1.6" fill="none" /><path d="M3 8 L21 8 M3 12 L21 12 M3 16 L21 16" stroke="currentColor" strokeWidth="1" opacity="0.5" /></>}
                  label="Bay Zones"
                  shortcut="B"
                />

                {/* Wall Measurements — cyan */}
                <LabelRow
                  active={shop.showWallMeasurements !== false}
                  onClick={() => setShop && setShop(s => ({ ...s, showWallMeasurements: !(s.showWallMeasurements !== false) }))}
                  iconColor="#00ccff"
                  iconPath={<><path d="M3 7 H21 M5 5 V9 M19 5 V9 M9 5 V9 M15 5 V9" stroke="currentColor" strokeWidth="1.6" /><path d="M3 17 H21 M5 15 V19 M19 15 V19" stroke="currentColor" strokeWidth="1.6" /></>}
                  label="Wall Measurements"
                  shortcut="M"
                />

                {/* Door Labels — green */}
                <LabelRow
                  active={shop.showDoorLabels !== false}
                  onClick={() => setShop && setShop(s => ({ ...s, showDoorLabels: !(s.showDoorLabels !== false) }))}
                  iconColor="#00cc88"
                  iconPath={<><path d="M5 21 V5 L15 3 V21 Z M5 21 H19" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinejoin="round" /><circle cx="12" cy="13" r="0.8" fill="currentColor" /></>}
                  label="Door Labels"
                  shortcut="D"
                />
              </div>
            </>
          )}
        </div>

        {/* ===== MEASURE — stays separate (it's a MODE, not a label) ===== */}
        <button
          onClick={() => { setDimMode(m => !m); setDimStart(null); setSelected([]); setSelectedDrain(null); setSelectedDim(null); }}
          title="Measure tool — click two points to draw a dimension line"
          style={{
            background: dimMode ? 'rgba(0,255,136,0.12)' : 'transparent',
            border: `1px solid ${dimMode ? '#00ff8866' : '#1e3a54'}`,
            borderRadius: 4, color: dimMode ? '#00ff88' : '#8aa8c5',
            fontSize: 11, fontWeight: 600, padding: '4px 10px',
            cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M3 17 L17 3 L21 7 L7 21 Z" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
            <path d="M7 13 L9 15 M10 10 L12 12 M13 7 L15 9" stroke="currentColor" strokeWidth="1.4" />
          </svg>
          Measure
        </button>

        <div style={{ width: 1, height: 16, background: '#162840', flexShrink: 0 }} />

        {/* ===== SHORTCUTS DROP-UP =================================
            Replaces the 4-span keyboard hint text that used to live here.
            Shows the same info but compactly with keycap-styled badges. */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={() => { setShowShortcutsMenu(m => !m); setShowSnapMenu(false); setShowLabelsMenu(false); }}
            title="Keyboard & mouse shortcuts"
            style={{
              background: 'transparent', border: '1px solid #1e3a54',
              borderRadius: 4, color: '#8aa8c5',
              fontSize: 11, fontWeight: 600, padding: '4px 10px',
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
            {/* Keyboard icon */}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="2" y="6" width="20" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.6" fill="none" />
              <path d="M6 10 H6.01 M10 10 H10.01 M14 10 H14.01 M18 10 H18.01 M7 14 H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            Shortcuts
            <span style={{ fontSize: 11, opacity: 0.6 }}>▴</span>
          </button>

          {showShortcutsMenu && (
            <>
              <div onClick={() => setShowShortcutsMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 998 }} />
              <div style={{
                position: 'absolute', bottom: 34, left: 0,
                background: '#0a1628', border: '1px solid #1e3a54',
                borderRadius: 8, padding: '8px 6px', minWidth: 260,
                boxShadow: '0 -4px 20px rgba(0,0,0,.6)', zIndex: 999,
              }}>
                <div style={{ fontSize: 11, color: '#7f9fbd', letterSpacing: '.07em', textTransform: 'uppercase', padding: '4px 10px 6px', fontWeight: 700 }}>
                  Keyboard &amp; Mouse
                </div>
                <ShortcutRow label="Multi-select"      keys="Shift + Click" />
                <ShortcutRow label="Copy / Paste"      keys="Ctrl+C / V" />
                <ShortcutRow label="Undo / Redo"       keys="Ctrl+Z / Y" />
                <ShortcutRow label="Lock / Unlock"     keys="L" />
                <ShortcutRow label="Cancel / Deselect" keys="ESC" />
                <div style={{ height: 1, background: '#162840', margin: '6px 8px' }} />
                <ShortcutRow label="Zoom"          keys="Scroll" accent />
                <ShortcutRow label="Pan"           keys="Alt + Drag" accent />
                <ShortcutRow label="Fit to screen" keys="0" />
              </div>
            </>
          )}
        </div>

        {/* ===== OVERLAP WARNING — only when real parts collide ===== */}
        {overlapUids.length > 0 && (
          <button
            onClick={() => { setSelected(overlapUids); setSelectedDrain(null); setSelectedDim(null); }}
            title="These items physically overlap (touching or very close is fine). Click to select them."
            style={{
              background: 'rgba(255,77,77,0.12)', border: '1px solid #ff4d4d88',
              borderRadius: 4, color: '#ff8080', fontSize: 11, fontWeight: 600,
              padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
            }}
          >
            ⚠ {overlapUids.length} items overlapping
          </button>
        )}

        {/* ===== ZOOM CLUSTER — unchanged, right-aligned ===== */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
          <button onClick={zoomOut} style={{ background: 'transparent', border: '1px solid #1e3a54', borderRadius: 4, color: '#8aa8c5', fontSize: 14, fontWeight: 600, padding: '1px 8px', cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1.4 }}>−</button>
          <div style={{ border: '1px solid #1e3a54', borderRadius: 4, background: 'transparent', color: '#8aa8c5', fontSize: 11, fontWeight: 600, padding: '3px 8px', minWidth: 44, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </div>
          <button onClick={zoomIn} style={{ background: 'transparent', border: '1px solid #1e3a54', borderRadius: 4, color: '#8aa8c5', fontSize: 14, fontWeight: 600, padding: '1px 8px', cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1.4 }}>+</button>
          <button onClick={resetZoom} style={{
            background: isAtDefault ? 'transparent' : 'rgba(0,80,180,.15)',
            border: `1px solid ${isAtDefault ? '#1e3a54' : '#3399ff88'}`,
            borderRadius: 4, color: isAtDefault ? '#2a4a6a' : '#3399ff',
            fontSize: 11, fontWeight: 600, padding: '3px 10px',
            cursor: 'pointer', fontFamily: 'inherit',
          }}>⊡ Fit</button>
        </div>
      </div>
    </div>
  );
});

export default CadCanvas;

// ---- Helper row components for the bottom-bar drop-up menus ---------------
// Kept at module scope (not inside the component) so they don't re-create
// on every parent render. Both are tiny and stateless.

// LabelRow — one row in the "Labels" drop-up. Behaves like a checkbox: clicking
// toggles `active` via the onClick callback but does NOT close the menu, so the
// user can flip multiple visibility settings in sequence.
function LabelRow({ active, onClick, iconColor, iconPath, label, shortcut }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', background: 'transparent',
        border: 'none', borderRadius: 5,
        // Active rows brighten the text; inactive rows mute it
        color: active ? '#c8dff0' : '#6a9fc0',
        fontSize: 11, fontWeight: 500, padding: '8px 10px',
        cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
        transition: 'background .12s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(51,153,255,.06)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {/* Colored icon — color is semantic per-toggle (cyan walls, amber bays, etc) */}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ color: iconColor, flexShrink: 0 }}>
        {iconPath}
      </svg>
      <span style={{ flex: 1 }}>{label}</span>
      {/* Checkmark only when active. Reserves the slot either way so labels align */}
      <span style={{ width: 14, display: 'flex', justifyContent: 'center' }}>
        {active && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ color: iconColor }}>
            <path d="M5 12 L10 17 L20 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      {/* Keyboard shortcut hint, small + muted */}
      {shortcut && (
        <span style={{ color: '#7f9fbd', fontSize: 11, fontFamily: 'monospace', minWidth: 12, textAlign: 'right' }}>
          {shortcut}
        </span>
      )}
    </button>
  );
}

// ShortcutRow — one row in the "Shortcuts" drop-up. Pure display, no interaction.
// `accent` colors the keycap blue (used for mouse-style hints like Scroll/Drag).
function ShortcutRow({ label, keys, accent = false }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '5px 10px', fontSize: 11, color: '#c8dff0',
    }}>
      <span style={{ flex: 1 }}>{label}</span>
      <span style={{
        background: '#07111e',
        border: '1px solid #1e3a54',
        borderRadius: 4,
        padding: '2px 6px',
        color: accent ? '#3399ff' : '#6a9fc0',
        fontFamily: 'monospace',
        fontSize: 11,
        whiteSpace: 'nowrap',
      }}>
        {keys}
      </span>
    </div>
  );
}